import Hydrophilicity2 from '../../Images/Hydrophilic.jpg'


// Hydrophilicity in Fluid Dynamics

const S = {
  wrapper: { marginTop:'10px', padding:'40px', border:'3px solid grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #c0392b', paddingBottom:'8px', marginBottom:'24px', color:'#7B0D0D' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#c0392b', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #c0392b', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#FEF0F0', border:'2px solid #c0392b', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#7B0D0D', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #c0392b' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#c0392b', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff5f5' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #f0cece', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#7B0D0D', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#fffafa', border:'1px solid #f0cece', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#7B0D0D', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Explanation / Formula</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ── Diagram 1: Contact Angle Spectrum ────────────────────────────
function DiagramContactAngleSpectrum() {
  const W = 900, H = 320;

  // Five droplet profiles across the wettability spectrum
  const cases = [
    { x: 60,  theta: 20,  label: 'Superhydrophilic', sub: 'θ < 10°',    color: '#2980b9', fill: '#aed6f1' },
    { x: 230, theta: 50,  label: 'Hydrophilic',       sub: 'θ = 10–90°', color: '#27ae60', fill: '#a9dfbf' },
    { x: 400, theta: 90,  label: 'Neutral',            sub: 'θ = 90°',    color: '#e07b00', fill: '#fad7a0' },
    { x: 570, theta: 130, label: 'Hydrophobic',        sub: 'θ = 90–150°',color: '#9b59b6', fill: '#d7bde2' },
    { x: 740, theta: 160, label: 'Superhydrophobic',   sub: 'θ > 150°',   color: '#c0392b', fill: '#f5b7b1' },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Wettability Spectrum — Contact Angle Classification</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="caArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#555"/>
          </marker>
          <marker id="caArrR" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
        </defs>

        {/* Wettability axis */}
        <line x1={30} y1={290} x2={870} y2={290} stroke="#333" strokeWidth="2" markerEnd="url(#caArr)"/>
        <text x={20}  y={294} textAnchor="end"    fontSize="11" fill="#2980b9" fontWeight="700">Hydrophilic</text>
        <text x={880} y={294} fontSize="11" fill="#c0392b" fontWeight="700">Hydrophobic</text>

        {cases.map(({ x, theta, label, sub, color, fill }) => {
          const baseY = 230;
          const cx = x + 65;         // droplet centre x
          const rad = 52;            // nominal radius for drawing
          const thetaRad = theta * Math.PI / 180;

          // For a sessile droplet with contact angle θ:
          // The contact line sits at y = baseY.
          // The sphere centre is at (cx, baseY - R·cosθ) where R = droplet radius.
          // The contact half-width = R·sinθ.
          // We draw a circular arc from the sphere clipped above the surface.
          const R = rad / Math.max(Math.sin(thetaRad), 0.08);
          const centreY = baseY - R * Math.cos(thetaRad);
          const halfWidth = R * Math.sin(thetaRad);

          // Arc: from left contact point to right contact point going up through apex
          const lx = cx - halfWidth, rx = cx + halfWidth;
          const ly = baseY, ry = baseY;

          // Large arc flag: if theta > 180 use 1, else use 0 for the short arc above baseline
          // For theta < 90: small cap above surface (arc flag = 0 going left to right means minor arc)
          // For theta > 90: dome (arc flag = 1)
          const largeArc = theta > 90 ? 1 : 0;

          const dropPath = `M ${lx},${ly} A ${R},${R} 0 ${largeArc},1 ${rx},${ry}`;

          return (
            <g key={label}>
              {/* Surface line */}
              <line x1={x} y1={baseY} x2={x + 130} y2={baseY} stroke="#888" strokeWidth="2"/>
              {/* Surface hatching below */}
              {[0,1,2,3,4,5,6].map(i => (
                <line key={i} x1={x + i*18} y1={baseY} x2={x + i*18 - 10} y2={baseY + 14}
                  stroke="#bbb" strokeWidth="1.2"/>
              ))}

              {/* Droplet fill */}
              <path d={`${dropPath} Z`} fill={fill} opacity="0.7" stroke="none"/>
              {/* Droplet outline */}
              <path d={dropPath} fill="none" stroke={color} strokeWidth="2.5"/>
              {/* Base line of droplet */}
              <line x1={lx} y1={baseY} x2={rx} y2={baseY} stroke={color} strokeWidth="2.5"/>

              {/* Contact angle arc — drawn at the left contact point */}
              {(()=>{
                const arcR = 22;
                // The tangent to the circle at (lx, ly) makes angle θ with the surface
                // Arc from 0° (along surface to right) sweeping up to θ
                const endX = lx + arcR * Math.cos(Math.PI - thetaRad);
                const endY = ly - arcR * Math.sin(Math.PI - thetaRad);
                const la = theta > 180 ? 1 : 0;
                return <>
                  <path d={`M ${lx + arcR},${ly} A ${arcR},${arcR} 0 ${la},0 ${endX},${endY}`}
                    fill="none" stroke={color} strokeWidth="1.5"/>
                  <text x={lx + arcR + 4} y={ly - 6} fontSize="12" fill={color} fontWeight="700">θ</text>
                </>;
              })()}

              {/* Label */}
              <text x={cx} y={baseY + 28} textAnchor="middle" fontSize="12" fill={color} fontWeight="700">{label}</text>
              <text x={cx} y={baseY + 42} textAnchor="middle" fontSize="11" fill={color}>{sub}</text>

              {/* Example material */}
              {[
                'Glass, TiO₂',
                'Cotton, silica',
                'PMMA, nylon',
                'PDMS, wax',
                'Lotus leaf, PTFE',
              ][cases.findIndex(c => c.x === x)] !== undefined && (
                <text x={cx} y={baseY + 56} textAnchor="middle" fontSize="10" fill="#777">
                  {['Glass, TiO₂','Cotton, silica','PMMA, nylon','PDMS, wax','Lotus leaf, PTFE'][cases.findIndex(c => c.x === x)]}
                </text>
              )}
            </g>
          );
        })}

        {/* θ = 90° boundary marker */}
        <line x1={482} y1={40} x2={482} y2={288} stroke="#e07b00" strokeWidth="1.5" strokeDasharray="6,3"/>
        <text x={482} y={36} textAnchor="middle" fontSize="11" fill="#e07b00" fontWeight="700">θ = 90° boundary</text>

        {/* Young's equation label */}
        <rect x={300} y={42} width={300} height={26} rx={3} fill="#fff5f5" stroke="#f0cece"/>
        <text x={450} y={59} textAnchor="middle" fontSize="12" fill="#7B0D0D" fontWeight="700">
          Young's equation: cos θ = (σ_SG − σ_SL) / σ_LG
        </text>
      </svg>
      <div style={S.dCaption}>The contact angle θ is the angle measured through the liquid at the three-phase contact line where solid, liquid, and gas meet. It is the primary macroscopic measure of wettability. Surfaces with θ below 90° are hydrophilic — water spreads readily; those with θ above 90° are hydrophobic — water beads up. The extremes (θ below 10° for superhydrophilic, θ above 150° for superhydrophobic) involve surface nano-texture that amplifies the intrinsic chemistry. Young's equation relates the equilibrium contact angle to the three interfacial energies at the three-phase contact line.</div>
    </div>
  );
}

// ── Diagram 2: Molecular Origin — Intermolecular Forces ──────────
function DiagramMolecularOrigin() {
  const W = 900, H = 300;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Molecular Origin of Hydrophilicity — Hydrogen Bonding and Surface Energy</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="moArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="moArrB" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#2980b9"/>
          </marker>
          <marker id="moArrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
        </defs>

        {/* Left: Water molecule H-bond network */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Water — Hydrogen Bond Network</text>

        {(()=>{
          // Draw 5 water molecules in a H-bond network
          const mols = [
            { cx:200, cy:140, angle: 0 },
            { cx:310, cy:105, angle: 30 },
            { cx:310, cy:185, angle:-30 },
            { cx:100, cy:110, angle: 60 },
            { cx:100, cy:180, angle:-20 },
          ];

          const drawWater = (cx, cy, angleDeg) => {
            const a = angleDeg * Math.PI / 180;
            const bondLen = 26;
            const HOH = 104.5 * Math.PI / 180; // bond angle
            // Two O-H bonds
            const h1x = cx + bondLen * Math.cos(a + HOH/2);
            const h1y = cy + bondLen * Math.sin(a + HOH/2);
            const h2x = cx + bondLen * Math.cos(a - HOH/2);
            const h2y = cy + bondLen * Math.sin(a - HOH/2);

            return (
              <g key={`${cx}-${cy}`}>
                <line x1={cx} y1={cy} x2={h1x} y2={h1y} stroke="#555" strokeWidth="2.5"/>
                <line x1={cx} y1={cy} x2={h2x} y2={h2y} stroke="#555" strokeWidth="2.5"/>
                <circle cx={cx} cy={cy} r={14} fill="#c0392b" opacity="0.85"/>
                <text x={cx} y={cy+5} textAnchor="middle" fontSize="11" fill="#fff" fontWeight="700">O</text>
                <circle cx={h1x} cy={h1y} r={8} fill="#aed6f1" stroke="#2980b9" strokeWidth="1.5"/>
                <text x={h1x} y={h1y+4} textAnchor="middle" fontSize="9" fill="#1a3a5c" fontWeight="700">H</text>
                <circle cx={h2x} cy={h2y} r={8} fill="#aed6f1" stroke="#2980b9" strokeWidth="1.5"/>
                <text x={h2x} y={h2y+4} textAnchor="middle" fontSize="9" fill="#1a3a5c" fontWeight="700">H</text>
              </g>
            );
          };

          return <>
            {/* H-bond dashed lines between molecules */}
            <line x1={225} y1={133} x2={290} y2={115} stroke="#27ae60" strokeWidth="2" strokeDasharray="5,3"/>
            <line x1={225} y1={150} x2={288} y2={172} stroke="#27ae60" strokeWidth="2" strokeDasharray="5,3"/>
            <line x1={170} y1={132} x2={118} y2={118} stroke="#27ae60" strokeWidth="2" strokeDasharray="5,3"/>
            <line x1={170} y1={152} x2={118} y2={170} stroke="#27ae60" strokeWidth="2" strokeDasharray="5,3"/>

            {mols.map(({ cx, cy, angle }) => drawWater(cx, cy, angle))}

            {/* H-bond label */}
            <text x={200} y={52} textAnchor="middle" fontSize="12" fill="#27ae60" fontWeight="700">Hydrogen bonds</text>
            <line x1={200} y1={56} x2={200} y2={68} stroke="#27ae60" strokeWidth="1.5" markerEnd="url(#moArrG)"/>

            {/* δ+ δ- labels */}
            <text x={56} y={208} fontSize="11" fill="#c0392b">δ⁻ (O — electronegative)</text>
            <text x={56} y={222} fontSize="11" fill="#2980b9">δ⁺ (H — electropositive)</text>

            {/* Dipole arrow on central molecule */}
            <line x1={200} y1={155} x2={200} y2={190} stroke="#e07b00" strokeWidth="2" markerEnd="url(#moArr)"/>
            <text x={210} y={185} fontSize="11" fill="#e07b00" fontWeight="700">μ (dipole)</text>
            <text x={210} y={198} fontSize="10" fill="#e07b00">1.85 D for H₂O</text>

            <rect x={14} y={240} width={PW-22} height={42} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1"/>
            <text x={20} y={256} fontSize="11" fill="#145214" fontWeight="700">H-bond energy: ~20 kJ/mol  (10× van der Waals)</text>
            <text x={20} y={271} fontSize="11" fill="#444">Each water molecule: up to 4 H-bonds simultaneously</text>
            <text x={20} y={279} fontSize="10" fill="#444">Creates tetrahedral network → anomalous properties</text>
          </>;
        })()}

        {/* Right: Surface energy vs wettability */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Surface Energy and Wettability</text>

        {(()=>{
          const ox = PW + 30;
          const materials = [
            { name:'TiO₂ (anatase)',    γ:70,  color:'#2980b9', note:'superhydrophilic' },
            { name:'Glass (SiO₂)',      γ:64,  color:'#27ae60', note:'hydrophilic' },
            { name:'Nylon',             γ:46,  color:'#e07b00', note:'moderate' },
            { name:'PMMA',              γ:41,  color:'#9b59b6', note:'moderate' },
            { name:'Polystyrene',       γ:33,  color:'#c0392b', note:'hydrophobic' },
            { name:'PDMS',              γ:22,  color:'#c0392b', note:'hydrophobic' },
            { name:'PTFE (Teflon)',     γ:18,  color:'#7B0D0D', note:'superhydrophobic' },
          ];
          const maxG = 75;
          const barH = 22, gap = 6, startY = 42;
          const barMaxW = PW - 90;

          return <>
            {/* Water surface energy reference */}
            <line x1={ox} y1={startY} x2={ox + barMaxW * 72.8/maxG} y2={startY}
              stroke="#2980b9" strokeWidth="2" strokeDasharray="5,3"/>
            <text x={ox + barMaxW * 72.8/maxG + 4} y={startY + 4} fontSize="10" fill="#2980b9" fontWeight="700">
              γ_water = 72.8 mJ/m²
            </text>

            {materials.map(({ name, γ, color, note }, i) => {
              const y = startY + 18 + i * (barH + gap);
              const bw = barMaxW * γ / maxG;
              return (
                <g key={name}>
                  <rect x={ox} y={y} width={bw} height={barH} rx={3}
                    fill={color} opacity="0.25" stroke={color} strokeWidth="1.5"/>
                  <rect x={ox} y={y} width={bw} height={barH} rx={3}
                    fill={color} opacity="0.15"/>
                  <text x={ox + 6} y={y + barH - 7} fontSize="11" fill="#333" fontWeight="600">{name}</text>
                  <text x={ox + bw + 5} y={y + barH - 7} fontSize="11" fill={color} fontWeight="700">{γ}</text>
                  <text x={ox + bw + 30} y={y + barH - 7} fontSize="10" fill="#777">{note}</text>
                </g>
              );
            })}

            {/* X axis */}
            <line x1={ox} y1={startY + 18 + 7*(barH+gap) - 4} x2={ox + barMaxW + 10} y2={startY + 18 + 7*(barH+gap) - 4}
              stroke="#333" strokeWidth="1.5" markerEnd="url(#moArr)"/>
            <text x={ox + barMaxW/2} y={startY + 18 + 7*(barH+gap) + 12}
              textAnchor="middle" fontSize="11" fill="#333">Surface energy γ (mJ/m²)</text>

            {/* Rule of thumb */}
            <rect x={ox} y={startY + 18 + 7*(barH+gap) + 22} width={PW-40} height={28} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={ox+6} y={startY + 18 + 7*(barH+gap) + 34} fontSize="11" fill="#7B0D0D" fontWeight="700">
              Rule: γ_surface &gt; γ_water / 2 → wetting tends to occur
            </text>
            <text x={ox+6} y={startY + 18 + 7*(barH+gap) + 46} fontSize="10" fill="#444">
              High surface energy → strong attraction to polar water molecules
            </text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>Hydrophilicity has a molecular origin: water molecules possess a large permanent dipole (1.85 D) and can form up to four hydrogen bonds simultaneously (each ~20 kJ/mol — roughly ten times stronger than London dispersion forces). A surface is hydrophilic when its atoms or functional groups can participate in hydrogen bonding or strong polar interactions with water, lowering the solid-liquid interfacial energy σ_SL and thus reducing the contact angle (Young's equation). Surfaces with high surface energy (metals, oxides, glass, polar polymers) attract water strongly. Low-surface-energy materials (fluoropolymers, hydrocarbons, PDMS) cannot H-bond and are hydrophobic.</div>
    </div>
  );
}

// ── Diagram 3: Young, Wenzel & Cassie-Baxter Models ──────────────
function DiagramWettingModels() {
  const W = 900, H = 330;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Wetting Models — Young (Flat), Wenzel (Rough), and Cassie-Baxter (Composite)</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="wmArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="wmArrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
          <marker id="wmArrO" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#e07b00"/>
          </marker>
        </defs>

        {/* ── Panel A: Young ── */}
        <rect x={10} y={10} width={270} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={145} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Young's Model</text>
        <text x={145} y={43} textAnchor="middle" fontSize="11" fill="#555">(ideal flat surface)</text>

        {(()=>{
          const cx=145, baseY=170, theta=60, R=62;
          const tRad = theta * Math.PI / 180;
          const centreY = baseY - R * Math.cos(tRad);
          const hw = R * Math.sin(tRad);

          return <>
            {/* Flat surface */}
            <line x1={20} y1={baseY} x2={270} y2={baseY} stroke="#555" strokeWidth="2.5"/>
            {[0,1,2,3,4,5,6,7,8,9,10,11].map(i=>(
              <line key={i} x1={20+i*22} y1={baseY} x2={10+i*22} y2={baseY+12}
                stroke="#bbb" strokeWidth="1.2"/>
            ))}
            <text x={145} y={baseY+26} textAnchor="middle" fontSize="11" fill="#555">Ideal flat, smooth</text>

            {/* Droplet */}
            <path d={`M ${cx-hw},${baseY} A ${R},${R} 0 ${theta>90?1:0},1 ${cx+hw},${baseY} Z`}
              fill="#aed6f1" opacity="0.6" stroke="#2980b9" strokeWidth="2.5"/>

            {/* Three surface tension vectors */}
            <line x1={cx-hw-38} y1={baseY} x2={cx-hw} y2={baseY}
              stroke="#27ae60" strokeWidth="2.5" markerEnd="url(#wmArrG)"/>
            <text x={cx-hw-44} y={baseY-8} textAnchor="end" fontSize="11" fill="#27ae60" fontWeight="700">σ_SG</text>

            <line x1={cx-hw+38} y1={baseY} x2={cx-hw} y2={baseY}
              stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#wmArr)"/>
            <text x={cx-hw+44} y={baseY+16} fontSize="11" fill="#c0392b" fontWeight="700">σ_SL</text>

            {/* LG vector along droplet surface */}
            {(()=>{
              const endX = cx-hw + 38*Math.cos(Math.PI-tRad);
              const endY = baseY - 38*Math.sin(Math.PI-tRad);
              return <>
                <line x1={cx-hw} y1={baseY} x2={endX} y2={endY}
                  stroke="#e07b00" strokeWidth="2.5" markerEnd="url(#wmArrO)"/>
                <text x={endX-2} y={endY-8} textAnchor="end" fontSize="11" fill="#e07b00" fontWeight="700">σ_LG</text>
              </>;
            })()}

            {/* θ_Y arc */}
            <path d={`M ${cx-hw+22},${baseY} A 22,22 0 0,0 ${cx-hw+22*Math.cos(Math.PI-tRad)},${baseY-22*Math.sin(Math.PI-tRad)}`}
              fill="none" stroke="#9b59b6" strokeWidth="1.8"/>
            <text x={cx-hw+30} y={baseY-18} fontSize="13" fill="#9b59b6" fontWeight="700">θ_Y</text>

            {/* Young's equation */}
            <rect x={16} y={240} width={252} height={62} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={22} y={256} fontSize="11" fill="#7B0D0D" fontWeight="700">Young's equation:</text>
            <text x={22} y={273} fontSize="12" fill="#333" fontFamily="'Courier New',monospace">cos θ_Y = (σ_SG−σ_SL) / σ_LG</text>
            <text x={22} y={289} fontSize="10" fill="#555">Valid only for flat, rigid, chemically</text>
            <text x={22} y={301} fontSize="10" fill="#555">homogeneous, non-deformable surfaces</text>
          </>;
        })()}

        {/* ── Panel B: Wenzel ── */}
        <rect x={295} y={10} width={295} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={442} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Wenzel Model</text>
        <text x={442} y={43} textAnchor="middle" fontSize="11" fill="#555">(liquid fills roughness — pinned)</text>

        {(()=>{
          const cx=442, baseY=165, roughH=18, nPeaks=8;
          // Rough surface with rectangular pillars filled by water
          const peakW = 22, valleyW = 14;
          const pitchW = peakW + valleyW;
          const surfStartX = 305, surfEndX = 582;

          // Build rough surface path
          let roughPath = `M ${surfStartX},${baseY}`;
          for(let i=0;i<nPeaks;i++){
            const px = surfStartX + i * pitchW;
            roughPath += ` L ${px},${baseY} L ${px},${baseY-roughH} L ${px+peakW},${baseY-roughH} L ${px+peakW},${baseY}`;
          }
          roughPath += ` L ${surfEndX},${baseY}`;

          // Water fills the rough surface — outline follows rough profile
          const wetPath = roughPath + ` L ${surfEndX},${baseY+20} L ${surfStartX},${baseY+20} Z`;

          return <>
            {/* Rough surface fill */}
            <path d={roughPath + ` L ${surfEndX},${baseY+20} L ${surfStartX},${baseY+20} Z`}
              fill="#c8a46e" opacity="0.4"/>
            <path d={roughPath} fill="none" stroke="#8B6914" strokeWidth="2"/>

            {/* Water filling valleys (Wenzel — fully wet) */}
            {Array.from({length:nPeaks},(_,i)=>{
              const px = surfStartX + i * pitchW;
              return <rect key={i} x={px+peakW} y={baseY-roughH} width={valleyW} height={roughH}
                fill="#aed6f1" opacity="0.7"/>;
            })}

            {/* Droplet on rough surface */}
            {(()=>{
              const theta=75, R=68;
              const tRad=theta*Math.PI/180;
              const hw=R*Math.sin(tRad);
              return <>
                <path d={`M ${cx-hw},${baseY-roughH} A ${R},${R} 0 ${theta>90?1:0},1 ${cx+hw},${baseY-roughH} Z`}
                  fill="#aed6f1" opacity="0.65" stroke="#2980b9" strokeWidth="2.5"/>
                {/* Apparent angle arc */}
                <path d={`M ${cx-hw+22},${baseY-roughH} A 22,22 0 0,0 ${cx-hw+22*Math.cos(Math.PI-tRad)},${baseY-roughH-22*Math.sin(Math.PI-tRad)}`}
                  fill="none" stroke="#9b59b6" strokeWidth="1.8"/>
                <text x={cx-hw+32} y={baseY-roughH-16} fontSize="12" fill="#9b59b6" fontWeight="700">θ_W</text>
              </>;
            })()}

            {/* Roughness factor illustration */}
            <line x1={305} y1={baseY+34} x2={582} y2={baseY+34} stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,2"/>
            <text x={442} y={baseY+48} textAnchor="middle" fontSize="10" fill="#c0392b">Projected area (flat equivalent)</text>
            <line x1={305} y1={baseY+56} x2={582} y2={baseY+56} stroke="#8B6914" strokeWidth="1.5"/>
            <text x={442} y={baseY+70} textAnchor="middle" fontSize="10" fill="#8B6914">Actual wetted area (r × projected)</text>

            {/* Wenzel equation */}
            <rect x={301} y={240} width={283} height={62} rx={3} fill="#fff5e6" stroke="#e07b00" strokeWidth="1"/>
            <text x={307} y={256} fontSize="11" fill="#8B4513" fontWeight="700">Wenzel equation:</text>
            <text x={307} y={272} fontSize="12" fill="#333" fontFamily="'Courier New',monospace">cos θ_W = r · cos θ_Y</text>
            <text x={307} y={288} fontSize="10" fill="#555">r = actual area / projected area ≥ 1</text>
            <text x={307} y={300} fontSize="10" fill="#555">Roughness amplifies wettability: if θ_Y&lt;90°</text>
            <text x={307} y={312} fontSize="10" fill="#555">→ θ_W &lt; θ_Y  (more hydrophilic)</text>
          </>;
        })()}

        {/* ── Panel C: Cassie-Baxter ── */}
        <rect x={605} y={10} width={285} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={747} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Cassie-Baxter Model</text>
        <text x={747} y={43} textAnchor="middle" fontSize="11" fill="#555">(air pockets trapped — composite)</text>

        {(()=>{
          const cx=747, baseY=165, roughH=20, nPeaks=6;
          const peakW = 24, valleyW = 18;
          const pitchW = peakW + valleyW;
          const surfStartX = 615, surfEndX = 885;

          let roughPath = `M ${surfStartX},${baseY}`;
          for(let i=0;i<nPeaks;i++){
            const px = surfStartX + i*pitchW;
            roughPath += ` L ${px},${baseY} L ${px},${baseY-roughH} L ${px+peakW},${baseY-roughH} L ${px+peakW},${baseY}`;
          }
          roughPath += ` L ${surfEndX},${baseY}`;

          return <>
            {/* Pillar surface */}
            <path d={roughPath + ` L ${surfEndX},${baseY+20} L ${surfStartX},${baseY+20} Z`}
              fill="#c8a46e" opacity="0.4"/>
            <path d={roughPath} fill="none" stroke="#8B6914" strokeWidth="2"/>

            {/* Air pockets in valleys (NOT filled with water) */}
            {Array.from({length:nPeaks},(_,i)=>{
              const px = surfStartX + i*pitchW;
              return <g key={i}>
                <rect x={px+peakW} y={baseY-roughH} width={valleyW} height={roughH}
                  fill="#f0f0f0" opacity="0.9" stroke="none"/>
                <text x={px+peakW+valleyW/2} y={baseY-roughH/2+4} textAnchor="middle"
                  fontSize="8" fill="#999">air</text>
              </g>;
            })}

            {/* Droplet resting on pillar tops */}
            {(()=>{
              const theta=145, R=70;
              const tRad=theta*Math.PI/180;
              const hw=R*Math.sin(tRad);
              return <>
                <path d={`M ${cx-hw},${baseY-roughH} A ${R},${R} 0 ${theta>90?1:0},1 ${cx+hw},${baseY-roughH} Z`}
                  fill="#aed6f1" opacity="0.65" stroke="#2980b9" strokeWidth="2.5"/>
                {/* θ_CB arc */}
                <path d={`M ${cx-hw+26},${baseY-roughH} A 26,26 0 1,0 ${cx-hw+26*Math.cos(Math.PI-tRad)},${baseY-roughH-26*Math.sin(Math.PI-tRad)}`}
                  fill="none" stroke="#9b59b6" strokeWidth="1.8"/>
                <text x={cx-hw-4} y={baseY-roughH-28} fontSize="12" fill="#9b59b6" fontWeight="700">θ_CB</text>
              </>;
            })()}

            {/* Cassie-Baxter equation */}
            <rect x={611} y={240} width={273} height={72} rx={3} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
            <text x={617} y={256} fontSize="11" fill="#145214" fontWeight="700">Cassie-Baxter equation:</text>
            <text x={617} y={272} fontSize="12" fill="#333" fontFamily="'Courier New',monospace">cos θ_CB = f_s cosθ_Y − (1−f_s)</text>
            <text x={617} y={288} fontSize="10" fill="#555">f_s = solid fraction in contact with liquid</text>
            <text x={617} y={300} fontSize="10" fill="#555">1−f_s = air fraction (cos θ_air = −1)</text>
            <text x={617} y={312} fontSize="10" fill="#555">Low f_s → very high θ_CB (superhydrophobic)</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>Three models describe wetting on real surfaces. Young's equation applies to ideal flat surfaces and provides the intrinsic (Young) contact angle θ_Y from the three interfacial energies. On rough surfaces, if liquid completely fills the roughness (Wenzel state), the apparent contact angle θ_W = arccos(r·cosθ_Y): roughness amplifies hydrophilicity when θ_Y below 90° and hydrophobicity when θ_Y above 90°. In the Cassie-Baxter state, air is trapped in the valleys beneath the droplet, dramatically increasing the apparent contact angle — this is the mechanism behind superhydrophobic surfaces like the lotus leaf. The Wenzel and Cassie-Baxter states can be interconverted by pressure or vibration (the Cassie-Wenzel transition).</div>
    </div>
  );
}

// ── Diagram 4: Capillary Rise & Capillary Length ─────────────────
function DiagramCapillaryRise() {
  const W = 900, H = 310;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Capillary Rise — Jurin's Law, Capillary Length and Meniscus Shape</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="crArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="crArrB" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#2980b9"/>
          </marker>
          <marker id="crArrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
        </defs>

        {/* Left: Three tubes of different radii */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Capillary Rise — Effect of Tube Radius</text>

        {(()=>{
          const baseY = 260, reservoirY = 248;
          const tubes = [
            { cx:110, r:8,  h:160, color:'#c0392b', label:'r₁ (thin)' },
            { cx:220, r:18, h: 71, color:'#e07b00', label:'r₂ (medium)' },
            { cx:360, r:35, h: 37, color:'#27ae60', label:'r₃ (wide)' },
          ];

          return <>
            {/* Reservoir */}
            <rect x={30} y={reservoirY} width={390} height={42} rx={3}
              fill="#aed6f1" stroke="#2980b9" strokeWidth="1.5" opacity="0.5"/>
            <line x1={30} y1={reservoirY} x2={420} y2={reservoirY}
              stroke="#2980b9" strokeWidth="2"/>
            <text x={225} y={reservoirY+24} textAnchor="middle" fontSize="11" fill="#2980b9">Liquid reservoir (water)</text>

            {tubes.map(({ cx, r, h, color, label }) => {
              const riseTop = reservoirY - h;
              return (
                <g key={cx}>
                  {/* Tube walls */}
                  <line x1={cx-r} y1={30} x2={cx-r} y2={reservoirY} stroke="#555" strokeWidth="2.5"/>
                  <line x1={cx+r} y1={30} x2={cx+r} y2={reservoirY} stroke="#555" strokeWidth="2.5"/>

                  {/* Liquid column */}
                  <rect x={cx-r+2} y={riseTop+10} width={2*r-4} height={reservoirY-riseTop-10}
                    fill="#aed6f1" opacity="0.75"/>

                  {/* Meniscus (concave for wetting) */}
                  <path d={`M ${cx-r+2},${riseTop+10} Q ${cx},${riseTop-6} ${cx+r-2},${riseTop+10}`}
                    fill="#aed6f1" stroke="#2980b9" strokeWidth="2" opacity="0.9"/>

                  {/* Height arrow */}
                  <line x1={cx+r+8} y1={reservoirY} x2={cx+r+8} y2={riseTop+10}
                    stroke={color} strokeWidth="1.8"
                    markerEnd="url(#crArr)" markerStart="url(#crArr)"/>
                  <text x={cx+r+16} y={(reservoirY+riseTop+10)/2+4}
                    fontSize="12" fill={color} fontWeight="700">h</text>

                  {/* Label */}
                  <text x={cx} y={reservoirY+14} textAnchor="middle"
                    fontSize="11" fill={color} fontWeight="700">{label}</text>
                  <text x={cx} y={reservoirY+26} textAnchor="middle"
                    fontSize="10" fill={color}>r = {r} px</text>
                </g>
              );
            })}

            {/* h ∝ 1/r annotation */}
            <rect x={16} y={246} width={PW-26} height={0} rx={3}/>
            <rect x={16} y={248} width={PW-26} height={46} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={22} y={263} fontSize="12" fill="#7B0D0D" fontWeight="700">Jurin's Law:  h = 2σ cosθ / (ρgr)</text>
            <text x={22} y={279} fontSize="11" fill="#444">h inversely proportional to tube radius r</text>
            <text x={22} y={291} fontSize="11" fill="#444">Water in glass (θ≈20°): h ≈ 1.5 mm for r = 1 mm</text>
          </>;
        })()}

        {/* Right: Capillary length and meniscus */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Capillary Length and Meniscus Profile</text>

        {(()=>{
          const ox = PW+30;

          // Capillary length λ_c illustration
          // Draw a large pool with a curved meniscus near a wall
          const wallX = ox+60, poolY = 200, poolW = 340;

          return <>
            {/* Pool */}
            <rect x={wallX} y={poolY} width={poolW} height={60} rx={2}
              fill="#aed6f1" opacity="0.5" stroke="#2980b9" strokeWidth="1.5"/>

            {/* Wall */}
            <rect x={wallX-12} y={60} width={12} height={220} fill="#888" stroke="#555" strokeWidth="1"/>
            {/* Hatch */}
            {[0,1,2,3,4,5,6].map(i=>(
              <line key={i} x1={wallX-12} y1={72+i*22} x2={wallX-24} y2={80+i*22}
                stroke="#666" strokeWidth="1.2"/>
            ))}

            {/* Meniscus profile — exponential decay: y = λ_c * e^(-x/λ_c) */}
            {(()=>{
              const lambda = 70; // capillary length in px
              const pts=[];
              for(let dx=0;dx<=280;dx+=3){
                const rise = lambda * Math.exp(-dx/lambda);
                pts.push({x:wallX+dx, y:poolY-rise});
              }
              const path = pts.map(({x,y},i)=>`${i===0?'M':'L'} ${x},${y}`).join(' ');
              const flatY = poolY;
              const fillPath = path + ` L ${wallX+280},${flatY} Z`;

              return <>
                <path d={fillPath} fill="#aed6f1" opacity="0.7"/>
                <path d={path} fill="none" stroke="#2980b9" strokeWidth="2.5"/>

                {/* Capillary length arrow */}
                <line x1={wallX} y1={poolY+70} x2={wallX+lambda} y2={poolY+70}
                  stroke="#c0392b" strokeWidth="2"
                  markerEnd="url(#crArr)" markerStart="url(#crArr)"/>
                <text x={wallX+lambda/2} y={poolY+84} textAnchor="middle"
                  fontSize="12" fill="#c0392b" fontWeight="700">λ_c</text>

                {/* Max rise at wall */}
                <line x1={wallX+5} y1={poolY} x2={wallX+5} y2={poolY-lambda}
                  stroke="#27ae60" strokeWidth="2"
                  markerEnd="url(#crArrG)" markerStart="url(#crArrG)"/>
                <text x={wallX+16} y={poolY-lambda/2+4}
                  fontSize="11" fill="#27ae60" fontWeight="700">h_max</text>
              </>;
            })()}

            {/* Formula boxes */}
            <rect x={ox} y={240} width={PW-30} height={54} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={ox+6} y={256} fontSize="12" fill="#7B0D0D" fontWeight="700">Capillary length:  λ_c = √(σ / ρg)</text>
            <text x={ox+6} y={272} fontSize="11" fill="#444">Water at 20°C:  λ_c = √(0.0728 / 998×9.81) ≈ 2.73 mm</text>
            <text x={ox+6} y={288} fontSize="11" fill="#444">Meniscus decays exponentially beyond ~λ_c from wall</text>

            {/* Bond number note */}
            <rect x={ox} y={56} width={PW-30} height={38} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1"/>
            <text x={ox+6} y={71} fontSize="11" fill="#145214" fontWeight="700">Bond number Bo = L²/λ_c²  =  ρgL²/σ</text>
            <text x={ox+6} y={86} fontSize="11" fill="#444">Bo &lt;&lt; 1: surface tension dominates (microfluidics)</text>
            <text x={ox+6} y={86} fontSize="11" fill="#444"/>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>Capillary rise (Jurin's law, h = 2σcosθ/(ρgr)) is the most direct macroscopic demonstration of hydrophilicity: a hydrophilic tube draws water upward because lowering the solid-liquid interfacial area releases more energy than is stored in the elevated gravitational potential. The height is inversely proportional to tube radius — very thin tubes (xylem vessels in trees, diameter ~10 μm) can sustain columns of many metres. The capillary length λ_c = √(σ/ρg) ≈ 2.73 mm for water defines the length scale below which surface tension dominates gravity. The meniscus shape near a wall decays exponentially with characteristic length λ_c.</div>
    </div>
  );
}

// ── Diagram 5: Contact Angle Hysteresis ─────────────────────────
function DiagramHysteresis() {
  const W = 900, H = 300;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Contact Angle Hysteresis — Advancing, Receding and Pinning</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="hyArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="hyArrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
          <marker id="hyArrB" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#2980b9"/>
          </marker>
        </defs>

        {/* Left: Tilted droplet showing adv/rec angles */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Sliding Droplet on Tilted Surface</text>

        {(()=>{
          const baseY = 195;
          const tiltAngle = 15 * Math.PI / 180; // surface tilt

          // Draw tilted surface
          const sx = 30, ex = PW - 10;
          const sy = baseY + (sx - PW/2) * Math.tan(tiltAngle);
          const ey = baseY + (ex - PW/2) * Math.tan(tiltAngle);

          // Droplet centre (asymmetric: advancing end lower, receding end higher)
          const dcx = PW/2 + 20, dcy_base = baseY + (dcx - PW/2)*Math.tan(tiltAngle);
          // Asymmetric droplet
          const advTheta = 110 * Math.PI / 180;
          const recTheta = 60  * Math.PI / 180;
          const R = 52;

          const advHW = R * 0.85;
          const recHW = R * 0.70;

          return <>
            {/* Surface */}
            <line x1={sx} y1={sy} x2={ex} y2={ey} stroke="#555" strokeWidth="2.5"/>
            {[0,1,2,3,4,5,6,7,8,9].map(i=>(
              <line key={i} x1={sx+i*38} y1={sy+i*38*Math.tan(tiltAngle)} x2={sx+i*38-14} y2={sy+i*38*Math.tan(tiltAngle)+14*Math.cos(tiltAngle)}
                stroke="#bbb" strokeWidth="1.2"/>
            ))}
            {/* Tilt angle label */}
            <text x={ex-20} y={ey-14} fontSize="11" fill="#555">α (tilt)</text>

            {/* Gravity arrow */}
            <line x1={PW/2+80} y1={baseY-80} x2={PW/2+80} y2={baseY-46}
              stroke="#333" strokeWidth="2" markerEnd="url(#hyArr)"/>
            <text x={PW/2+90} y={baseY-62} fontSize="11" fill="#333" fontWeight="700">g</text>

            {/* Droplet — asymmetric blob */}
            <ellipse cx={dcx} cy={dcy_base-28} rx={advHW} ry={32}
              fill="#aed6f1" opacity="0.65" stroke="#2980b9" strokeWidth="2.5"
              transform={`rotate(${-tiltAngle*180/Math.PI},${dcx},${dcy_base-28})`}/>

            {/* Advancing angle arc (front of droplet, higher theta) */}
            <path d={`M ${dcx+advHW-8},${dcy_base-10} A 24,24 0 0,0 ${dcx+advHW-8+24*Math.cos(Math.PI-advTheta)},${dcy_base-10-24*Math.sin(Math.PI-advTheta)}`}
              fill="none" stroke="#c0392b" strokeWidth="2"/>
            <text x={dcx+advHW+10} y={dcy_base-22} fontSize="12" fill="#c0392b" fontWeight="700">θ_A</text>
            <text x={dcx+advHW+10} y={dcy_base-8}  fontSize="10" fill="#c0392b">advancing</text>

            {/* Receding angle arc (rear, lower theta) */}
            <path d={`M ${dcx-recHW+8},${dcy_base-10} A 22,22 0 0,0 ${dcx-recHW+8+22*Math.cos(Math.PI-recTheta)},${dcy_base-10-22*Math.sin(Math.PI-recTheta)}`}
              fill="none" stroke="#27ae60" strokeWidth="2"/>
            <text x={dcx-recHW-50} y={dcy_base-24} textAnchor="end" fontSize="12" fill="#27ae60" fontWeight="700">θ_R</text>
            <text x={dcx-recHW-50} y={dcy_base-10} textAnchor="end" fontSize="10" fill="#27ae60">receding</text>

            {/* Motion arrow */}
            <line x1={dcx-30} y1={dcy_base-80} x2={dcx+30} y2={dcy_base-80}
              stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#hyArr)"/>
            <text x={dcx} y={dcy_base-86} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">Sliding direction</text>

            {/* CAH box */}
            <rect x={16} y={228} width={PW-26} height={58} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={22} y={244} fontSize="12" fill="#7B0D0D" fontWeight="700">Hysteresis: Δθ = θ_A − θ_R</text>
            <text x={22} y={260} fontSize="11" fill="#444">Retention force: F = σ_LG w (cosθ_R − cosθ_A)</text>
            <text x={22} y={276} fontSize="11" fill="#444">Critical tilt α*: mg sinα = F_retention</text>
            <text x={22} y={288} fontSize="10" fill="#444">Low hysteresis → easy sliding; lotus leaf Δθ &lt; 5°</text>
          </>;
        })()}

        {/* Right: Hysteresis on chemically heterogeneous surface */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Origin of Hysteresis — Pinning Sites</text>

        {(()=>{
          const ox = PW+30, baseY = 180;
          // Show a surface with chemical defects and a pinned contact line
          return <>
            {/* Surface with patches */}
            <line x1={ox} y1={baseY} x2={ox+PW-20} y2={baseY} stroke="#555" strokeWidth="2.5"/>
            {/* Chemical heterogeneity patches */}
            {[ox+30, ox+90, ox+150, ox+220, ox+290, ox+350].map((px,i)=>(
              <rect key={i} x={px} y={baseY-8} width={30} height={8} rx={2}
                fill={i%2===0?'#c0392b':'#2980b9'} opacity="0.3"/>
            ))}
            <text x={ox+PW/2-20} y={baseY+16} textAnchor="middle" fontSize="10" fill="#555">
              Chemical patches (hydrophilic ↔ hydrophobic)
            </text>

            {/* Droplet with jagged contact line */}
            {(()=>{
              const cx = ox+PW/2-20, topY = baseY-70;
              return <>
                {/* Main droplet body */}
                <ellipse cx={cx} cy={topY} rx={82} ry={46}
                  fill="#aed6f1" opacity="0.55" stroke="#2980b9" strokeWidth="2"/>
                {/* Jagged contact line segments */}
                <path d={`M ${cx-82},${baseY} L ${cx-70},${baseY-4} L ${cx-55},${baseY+2} L ${cx-40},${baseY-5} L ${cx-25},${baseY+3} L ${cx-10},${baseY-4} L ${cx+5},${baseY} L ${cx+20},${baseY-5} L ${cx+35},${baseY+2} L ${cx+50},${baseY-4} L ${cx+65},${baseY+3} L ${cx+82},${baseY}`}
                  fill="none" stroke="#c0392b" strokeWidth="2.5"/>

                {/* Pin arrows at defect sites */}
                {[cx-55, cx+5, cx+50].map((px, i) => (
                  <g key={i}>
                    <line x1={px} y1={baseY+20} x2={px} y2={baseY+2}
                      stroke="#e07b00" strokeWidth="1.8" markerEnd="url(#hyArr)"/>
                    <text x={px} y={baseY+32} textAnchor="middle" fontSize="9" fill="#e07b00">pin</text>
                  </g>
                ))}
              </>;
            })()}

            {/* Roughness vs chemical hysteresis */}
            <rect x={ox} y={230} width={PW-30} height={58} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
            <text x={ox+6} y={246} fontSize="11" fill="#7B0D0D" fontWeight="700">Sources of hysteresis:</text>
            <text x={ox+6} y={262} fontSize="11" fill="#444">• Chemical heterogeneity (polar/non-polar patches)</text>
            <text x={ox+6} y={277} fontSize="11" fill="#444">• Surface roughness (geometric pinning at edges)</text>
            <text x={ox+6} y={284} fontSize="11" fill="#444">• Viscoelastic deformation (soft substrates)</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>When a droplet moves or inflates/deflates on a surface, the contact angle is not a single value but spans a range: the advancing angle θ_A (maximum before the contact line moves forward) and the receding angle θ_R (minimum before it retreats). The hysteresis Δθ = θ_A − θ_R arises from physical and chemical surface heterogeneities that pin the contact line. Low hysteresis is as important as high contact angle for self-cleaning: the lotus leaf achieves both θ above 150° and Δθ below 5° through its hierarchical wax nano-texture, allowing droplets to roll off at minimal tilt angles and carry away contaminant particles.</div>
    </div>
  );
}

// ── Diagram 6: Spreading and Wetting Dynamics ────────────────────
function DiagramSpreadingDynamics() {
  const W = 900, H = 300;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Droplet Spreading Dynamics — Tanner's Law and Spreading Coefficient</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="sdArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#333"/>
          </marker>
          <marker id="sdArrR" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="sdArrB" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#2980b9"/>
          </marker>
        </defs>

        {/* Left: Spreading sequence diagram */}
        {(()=>{
          const baseY = 195;
          const stages = [
            { t:'t=0', cx:90,  rx:30, ry:30, theta:90,  label:'Just placed' },
            { t:'t₁', cx:230, rx:52, ry:20, theta:55,  label:'Spreading' },
            { t:'t₂', cx:380, rx:75, ry:12, theta:22,  label:'Late stage' },
            { t:'t∞', cx:540, rx:100,ry:4,  theta:8,   label:'Equilibrium' },
          ];

          return <>
            {/* Surface line */}
            <line x1={30} y1={baseY} x2={650} y2={baseY} stroke="#555" strokeWidth="2.5"/>
            {[0,1,2,3,4,5,6,7,8,9,10,11,12,13,14,15].map(i=>(
              <line key={i} x1={30+i*40} y1={baseY} x2={22+i*40} y2={baseY+12}
                stroke="#bbb" strokeWidth="1.2"/>
            ))}

            {stages.map(({ t, cx, rx, ry, theta, label }) => (
              <g key={t}>
                {/* Droplet ellipse approximation */}
                <ellipse cx={cx} cy={baseY-ry} rx={rx} ry={ry}
                  fill="#aed6f1" opacity="0.65" stroke="#2980b9" strokeWidth="2"/>
                {/* Contact angle indicator */}
                <text x={cx+rx+8} y={baseY-4} fontSize="11" fill="#c0392b" fontWeight="700">{theta}°</text>
                {/* Time label */}
                <text x={cx} y={baseY+22} textAnchor="middle" fontSize="12" fill="#555" fontWeight="700">{t}</text>
                <text x={cx} y={baseY+36} textAnchor="middle" fontSize="10" fill="#777">{label}</text>
                {/* Radius arrow */}
                <line x1={cx} y1={baseY-ry/2-12} x2={cx+rx} y2={baseY-ry/2-12}
                  stroke="#e07b00" strokeWidth="1.5" markerEnd="url(#sdArr)" markerStart="url(#sdArr)"/>
                <text x={cx+rx/2} y={baseY-ry/2-18} textAnchor="middle" fontSize="10" fill="#e07b00">R</text>
              </g>
            ))}

            {/* Time arrow */}
            <line x1={30} y1={baseY+55} x2={640} y2={baseY+55}
              stroke="#333" strokeWidth="2" markerEnd="url(#sdArr)"/>
            <text x={340} y={baseY+70} textAnchor="middle" fontSize="12" fill="#333" fontWeight="700">Time →</text>
          </>;
        })()}

        {/* Right: Tanner's law log-log plot + spreading coefficient box */}
        {(()=>{
          const ox=670, oy=30, pw=210, ch=180;
          // R(t) ~ t^(1/10) Tanner's law: log-log plot
          const pts=[];
          for(let logt=-2; logt<=3; logt+=0.1){
            const logR = logt/10 + 1.8; // approximate
            pts.push({logt, logR});
          }
          const tx=(lt)=>ox+(lt+2)/5*pw;
          const ry=(lr)=>oy+ch-(lr-1)/1.8*ch;
          const path=pts.map(({logt,logR},i)=>`${i===0?'M':'L'} ${tx(logt)},${ry(logR)}`).join(' ');

          return <>
            <rect x={660} y={10} width={230} height={280} rx={5} fill="#fff" stroke="#f0cece"/>
            <text x={775} y={28} textAnchor="middle" fontSize="13" fill="#7B0D0D" fontWeight="700">Tanner's Law (log-log)</text>

            {/* Axes */}
            <line x1={ox} y1={oy+ch} x2={ox+pw+10} y2={oy+ch} stroke="#333" strokeWidth="1.5" markerEnd="url(#sdArr)"/>
            <line x1={ox} y1={oy}    x2={ox}        y2={oy+ch+6} stroke="#333" strokeWidth="1.5" markerEnd="url(#sdArr)"/>
            <text x={ox+pw+14} y={oy+ch+4} fontSize="11" fill="#333">log t</text>
            <text x={ox-8}     y={oy-4}    fontSize="11" fill="#333">log R</text>

            {/* Tanner's law line */}
            <path d={path} fill="none" stroke="#c0392b" strokeWidth="2.5"/>

            {/* Slope label */}
            <text x={tx(0.5)} y={ry(2.0)-10} fontSize="12" fill="#c0392b" fontWeight="700">slope = 1/10</text>
            <text x={tx(0.5)} y={ry(2.0)+4} fontSize="11" fill="#c0392b">R ~ t^(1/10)</text>

            {/* Spreading coefficient box */}
            <rect x={664} y={220} width={222} height={64} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={670} y={236} fontSize="11" fill="#7B0D0D" fontWeight="700">Spreading coefficient S:</text>
            <text x={670} y={252} fontSize="11" fill="#444" fontFamily="'Courier New',monospace">S = σ_SG − σ_SL − σ_LG</text>
            <text x={670} y={268} fontSize="10" fill="#444">S &gt; 0: complete wetting (θ → 0)</text>
            <text x={670} y={278} fontSize="10" fill="#444">S &lt; 0: partial wetting (finite θ)</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>When a droplet is placed on a hydrophilic surface (S above 0, complete wetting regime), it spreads spontaneously. The spreading dynamics follow three regimes: an initial inertial phase, a viscous-capillary phase, and a final diffuse (molecular) regime. In the viscous-capillary regime, the spreading radius follows Tanner's law R(t) ∝ (σ/μ)^(1/10) t^(1/10) — an unexpectedly slow power law arising from the balance between capillary driving force (proportional to θ³ for small angles) and viscous dissipation concentrated near the contact line. The spreading coefficient S = σ_SG − σ_SL − σ_LG determines whether spreading is complete (S above 0) or stops at a finite equilibrium angle (S below 0).</div>
    </div>
  );
}

// ── Diagram 7: Wicking and Imbibition in Porous Media ───────────
function DiagramWicking() {
  const W = 900, H = 300;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Wicking and Imbibition — Capillary-Driven Flow in Porous Media</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="wkArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="wkArrB" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#2980b9"/>
          </marker>
          <pattern id="poresPat" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
            <rect width="14" height="14" fill="#f0e8d8"/>
            <circle cx="7" cy="7" r="3" fill="#c8a46e" opacity="0.6"/>
          </pattern>
          <pattern id="wetPores" x="0" y="0" width="14" height="14" patternUnits="userSpaceOnUse">
            <rect width="14" height="14" fill="#aed6f1" opacity="0.6"/>
            <circle cx="7" cy="7" r="3" fill="#c8a46e" opacity="0.6"/>
          </pattern>
        </defs>

        {/* Left: Lucas-Washburn wicking column */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Lucas-Washburn Imbibition</text>

        {(()=>{
          const cx=PW/2+10, topY=40, botY=270, poreW=90;
          const frontY=140; // imbibition front

          return <>
            {/* Porous column */}
            <rect x={cx-poreW/2} y={topY} width={poreW} height={botY-topY}
              fill="url(#poresPat)" stroke="#8B6914" strokeWidth="2.5"/>

            {/* Wet region */}
            <rect x={cx-poreW/2+3} y={frontY} width={poreW-6} height={botY-frontY-3}
              fill="url(#wetPores)" opacity="0.9"/>
            <line x1={cx-poreW/2+3} y1={frontY} x2={cx+poreW/2-3} y2={frontY}
              stroke="#2980b9" strokeWidth="2.5" strokeDasharray="5,3"/>

            {/* Imbibition front label */}
            <text x={cx+poreW/2+10} y={frontY+4} fontSize="12" fill="#2980b9" fontWeight="700">L(t) front</text>
            <text x={cx+poreW/2+10} y={frontY+17} fontSize="11" fill="#2980b9">∝ √t</text>

            {/* L(t) dimension */}
            <line x1={cx-poreW/2-18} y1={botY} x2={cx-poreW/2-18} y2={frontY}
              stroke="#c0392b" strokeWidth="1.8" markerEnd="url(#wkArr)" markerStart="url(#wkArr)"/>
            <text x={cx-poreW/2-28} y={(botY+frontY)/2+4} textAnchor="end" fontSize="12" fill="#c0392b" fontWeight="700">L(t)</text>

            {/* Reservoir at bottom */}
            <rect x={cx-poreW/2-30} y={botY} width={poreW+60} height={20} rx={3}
              fill="#aed6f1" stroke="#2980b9" strokeWidth="1.5" opacity="0.6"/>
            <text x={cx} y={botY+14} textAnchor="middle" fontSize="10" fill="#2980b9">Liquid reservoir</text>

            {/* Dry / wet labels */}
            <text x={cx} y={topY+50} textAnchor="middle" fontSize="11" fill="#8B6914" fontWeight="700">Dry pores</text>
            <text x={cx} y={botY-30} textAnchor="middle" fontSize="11" fill="#2980b9" fontWeight="700">Wet pores</text>

            {/* Arrow showing direction */}
            <line x1={cx+poreW/2+12} y1={botY-20} x2={cx+poreW/2+12} y2={frontY+20}
              stroke="#27ae60" strokeWidth="2.5" markerEnd="url(#wkArr)"/>
            <text x={cx+poreW/2+22} y={(botY+frontY)/2+50} fontSize="11" fill="#27ae60" fontWeight="700">capillary</text>
            <text x={cx+poreW/2+22} y={(botY+frontY)/2+64} fontSize="11" fill="#27ae60" fontWeight="700">suction</text>

            {/* Formula */}
            <rect x={16} y={232} width={PW-26} height={52} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={22} y={248} fontSize="11" fill="#7B0D0D" fontWeight="700">Lucas-Washburn equation:</text>
            <text x={22} y={265} fontSize="12" fill="#333" fontFamily="'Courier New',monospace">L² = (r σ cosθ / 2μ) · t</text>
            <text x={22} y={281} fontSize="11" fill="#444">L ∝ √t;  valid for viscous-dominated imbibition</text>
          </>;
        })()}

        {/* Right: Darcy flow + 2D spreading on fabric */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">2D Wicking and Darcy Flow</text>

        {(()=>{
          const ox=PW+30, oy=44;
          const cx=ox+PW/2-20, cy=oy+90;

          return <>
            {/* Fabric / porous material (rectangle) */}
            <rect x={ox} y={oy} width={PW-20} height={200} rx={3}
              fill="url(#poresPat)" stroke="#8B6914" strokeWidth="2"/>

            {/* Radial wicking front — ellipses showing anisotropic spread */}
            {[20,42,65,88].map((r,i)=>(
              <ellipse key={i} cx={cx} cy={cy} rx={r*1.4} ry={r}
                fill="none" stroke="#2980b9" strokeWidth={1.5-i*0.2} strokeDasharray="4,3" opacity={0.5+i*0.12}/>
            ))}
            {/* Wet region fill */}
            <ellipse cx={cx} cy={cy} rx={88*1.4} ry={88}
              fill="#aed6f1" opacity="0.35" stroke="none"/>

            {/* Source point */}
            <circle cx={cx} cy={cy} r={6} fill="#c0392b" stroke="#fff" strokeWidth="1.5"/>
            <text x={cx} y={cy-10} textAnchor="middle" fontSize="11" fill="#c0392b" fontWeight="700">Liquid source</text>

            {/* Anisotropy arrows */}
            <line x1={cx} y1={cy} x2={cx+88*1.4} y2={cy}
              stroke="#e07b00" strokeWidth="2" strokeDasharray="4,2"/>
            <text x={cx+88*1.4+4} y={cy+4} fontSize="10" fill="#e07b00">k_x</text>
            <line x1={cx} y1={cy} x2={cx} y2={cy-88}
              stroke="#9b59b6" strokeWidth="2" strokeDasharray="4,2"/>
            <text x={cx+4} y={cy-90} fontSize="10" fill="#9b59b6">k_y</text>
            <text x={cx+4} y={cy-76} fontSize="10" fill="#9b59b6">(k_y &lt; k_x)</text>
            <text x={cx} y={cy+100} textAnchor="middle" fontSize="11" fill="#555">Anisotropic wicking</text>
            <text x={cx} y={cy+115} textAnchor="middle" fontSize="11" fill="#555">(fabric: warp vs weft)</text>

            {/* Darcy formula */}
            <rect x={ox} y={oy+208} width={PW-20} height={52} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1"/>
            <text x={ox+6} y={oy+224} fontSize="11" fill="#145214" fontWeight="700">Darcy's law:  u = −(K/μ)∇p</text>
            <text x={ox+6} y={oy+240} fontSize="11" fill="#444">K = permeability (m²);  capillary pressure p_c = 2σcosθ/r</text>
            <text x={ox+6} y={oy+254} fontSize="11" fill="#444">Washburn: driving p_c balanced by viscous resistance</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>Wicking is the spontaneous imbibition of a wetting liquid into a porous medium driven by capillary pressure. In a single cylindrical capillary, the Lucas-Washburn equation predicts the imbibition front position L(t) = √(rσcosθ/2μ · t): front penetration grows as √t because the capillary driving force is constant but viscous resistance increases as more liquid fills the pore. In porous media such as fabrics or paper, the equivalent result holds with effective radius and permeability replacing the single-tube parameters. Wicking is anisotropic in structured media (warp vs weft directions in textiles); this is exploited in moisture-management fabrics that wick sweat away from skin toward the outer surface for evaporation.</div>
    </div>
  );
}

// ── Diagram 8: Superhydrophobic Surfaces ─────────────────────────
function DiagramSuperhydrophobic() {
  const W = 900, H = 310;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Superhydrophobic Surfaces — Lotus Effect and Hierarchical Structure</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="shArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="shArrG" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#27ae60"/>
          </marker>
        </defs>

        {/* Left: Lotus effect schematic */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Lotus Effect — Hierarchical Roughness</text>

        {(()=>{
          const baseY=220;
          const nPillars=10;
          const pillarH=40, pillarW=18, pitch=38;
          const startX=22;

          return <>
            {/* Substrate */}
            <rect x={startX} y={baseY+pillarH} width={nPillars*pitch+pillarW} height={16} rx={2} fill="#888" stroke="#555" strokeWidth="1.5"/>

            {/* Micro-pillars */}
            {Array.from({length:nPillars},(_,i)=>{
              const px = startX + i*pitch;
              return (
                <g key={i}>
                  <rect x={px} y={baseY} width={pillarW} height={pillarH} rx={2}
                    fill="#c8a46e" stroke="#8B6914" strokeWidth="1.5"/>
                  {/* Nano-bumps on top of micro-pillars */}
                  {[2,6,10,14].map(j=>(
                    <ellipse key={j} cx={px+j} cy={baseY} rx={3} ry={2}
                      fill="#a0855a" stroke="none"/>
                  ))}
                  {/* Waxy coating dots */}
                  {[4,9].map(j=>(
                    <circle key={j} cx={px+j} cy={baseY-3} r={2}
                      fill="#e07b00" opacity="0.5"/>
                  ))}
                </g>
              );
            })}

            {/* Air pockets between pillars */}
            {Array.from({length:nPillars-1},(_,i)=>{
              const px = startX + i*pitch + pillarW;
              const nextPx = startX + (i+1)*pitch;
              return <text key={i} x={(px+nextPx)/2} y={baseY+pillarH/2+4}
                textAnchor="middle" fontSize="8" fill="#99a">air</text>;
            })}

            {/* Droplet on top — large contact angle */}
            {(()=>{
              const cx=startX+nPillars*pitch/2-10, R=72;
              const theta=155, tRad=theta*Math.PI/180;
              const hw=R*Math.sin(tRad);
              return <>
                <path d={`M ${cx-hw},${baseY} A ${R},${R} 0 1,1 ${cx+hw},${baseY} Z`}
                  fill="#aed6f1" opacity="0.65" stroke="#2980b9" strokeWidth="2.5"/>
                {/* θ annotation */}
                <path d={`M ${cx-hw+28},${baseY} A 28,28 0 1,0 ${cx-hw+28*Math.cos(Math.PI-tRad)},${baseY-28*Math.sin(Math.PI-tRad)}`}
                  fill="none" stroke="#9b59b6" strokeWidth="1.8"/>
                <text x={cx-hw-8} y={baseY-28} fontSize="13" fill="#9b59b6" fontWeight="700">θ&gt;150°</text>
              </>;
            })()}

            {/* Contaminant particle rolling off */}
            <circle cx={startX+nPillars*pitch-30} cy={baseY-82} r={9}
              fill="#8B6914" stroke="#555" strokeWidth="1.5"/>
            <text x={startX+nPillars*pitch-30} y={baseY-66} textAnchor="middle" fontSize="9" fill="#555">dirt</text>
            <line x1={startX+nPillars*pitch-30} y1={baseY-92} x2={startX+nPillars*pitch-10} y2={baseY-106}
              stroke="#c0392b" strokeWidth="1.5" markerEnd="url(#shArr)"/>

            {/* Scale bar and labels */}
            <text x={10+PW/2} y={baseY+50} textAnchor="middle" fontSize="11" fill="#555">Micro-pillars ~10 μm;  nano-bumps ~200 nm;  wax crystals ~100 nm</text>

            {/* Hierarchical label */}
            <rect x={16} y={260} width={PW-26} height={28} rx={3} fill="#e8f4e8" stroke="#27ae60" strokeWidth="1"/>
            <text x={22} y={276} fontSize="11" fill="#145214" fontWeight="700">Hierarchical: micro-scale traps air;  nano-scale lowers f_s → ultra-low CAH</text>
          </>;
        })()}

        {/* Right: Self-cleaning mechanism and applications */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Self-Cleaning Mechanism and Applications</text>

        {(()=>{
          const ox=PW+30;
          const steps=[
            { y:46,  fill:'#FEF0F0', stroke:'#c0392b',  t:'1. High θ (&gt;150°)',  s:'Droplet spherical — minimal contact area' },
            { y:90,  fill:'#fff5e6', stroke:'#e07b00',  t:'2. Low CAH (&lt;5°)',  s:'Droplet rolls at tiny tilt angle (~2–5°)' },
            { y:134, fill:'#f0fff4', stroke:'#27ae60',  t:'3. Contaminant pickup', s:'Rolling droplet adsorbs dirt particles' },
            { y:178, fill:'#f5e6ff', stroke:'#9b59b6',  t:'4. Particle removal', s:'Adhesion of dirt to droplet &gt; dirt to surface' },
          ];

          return <>
            {steps.map(({y,fill,stroke,t,s})=>(
              <g key={y}>
                <rect x={ox} y={y} width={PW-20} height={38} rx={3} fill={fill} stroke={stroke} strokeWidth="1.5"/>
                <text x={ox+8} y={y+15} fontSize="12" fill="#333" fontWeight="700">{t}</text>
                <text x={ox+8} y={y+30} fontSize="11" fill="#555">{s}</text>
              </g>
            ))}

            {/* Applications grid */}
            <rect x={ox} y={228} width={PW-20} height={60} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={ox+6} y={244} fontSize="11" fill="#7B0D0D" fontWeight="700">Applications:</text>
            <text x={ox+6} y={259} fontSize="11" fill="#444">• Self-cleaning glass, textiles, coatings</text>
            <text x={ox+6} y={273} fontSize="11" fill="#444">• Anti-icing aircraft surfaces (delays ice nucleation)</text>
            <text x={ox+6} y={285} fontSize="11" fill="#444">• Drag-reducing ship hulls (trapped air layer)</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>Superhydrophobic surfaces (θ above 150°, Δθ below 5°) achieve their remarkable properties through hierarchical surface texture — micron-scale pillars carrying nano-scale roughness — combined with low-surface-energy chemistry (waxy coatings). The microscale traps air (Cassie-Baxter state), dramatically reducing the solid fraction f_s in contact with the droplet. The nanoscale further reduces the contact area and pinning. The lotus leaf is the natural archetype: its micro-papillae (~10 μm) carry epicuticular wax nano-crystals (~100 nm), giving θ ≈ 162° and Δθ ≈ 3°. Self-cleaning occurs because rolling droplets have greater adhesion to contaminant particles than the contaminated surface does, physically removing them.</div>
    </div>
  );
}

// ── Diagram 9: Electrowetting ────────────────────────────────────
function DiagramElectrowetting() {
  const W = 900, H = 290;
  const PW = (W - 30) / 2;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Electrowetting on Dielectric (EWOD) — Voltage-Controlled Wettability</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>
        <defs>
          <marker id="ewArr" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#c0392b"/>
          </marker>
          <marker id="ewArrB" markerWidth="7" markerHeight="7" refX="5" refY="3.5" orient="auto">
            <polygon points="0 0, 6 3.5, 0 7" fill="#2980b9"/>
          </marker>
        </defs>

        {/* Left: EWOD schematic */}
        <rect x={10} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={10+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Electrowetting on Dielectric (EWOD)</text>

        {(()=>{
          const baseY=175;

          // Three states: V=0, V=medium, V=high
          const states=[
            {cx:100, theta:120, V:'V = 0',  color:'#c0392b'},
            {cx:230, theta: 80, V:'V = 20V', color:'#e07b00'},
            {cx:360, theta: 30, V:'V = 60V', color:'#27ae60'},
          ];

          return <>
            {/* Electrode layers */}
            <rect x={18} y={baseY}    width={420} height={10} rx={1} fill="#e07b00" opacity="0.7"/>
            <text x={444} y={baseY+8} fontSize="10" fill="#e07b00">Dielectric layer (insulator)</text>
            <rect x={18} y={baseY+10} width={420} height={12} rx={1} fill="#888" opacity="0.8"/>
            <text x={444} y={baseY+20} fontSize="10" fill="#888">Electrode</text>
            <rect x={18} y={baseY+22} width={420} height={8}  rx={1} fill="#555"/>
            <text x={444} y={baseY+30} fontSize="10" fill="#555">Ground plane</text>

            {/* Voltage source */}
            <line x1={18} y1={baseY+14} x2={18} y2={baseY+55} stroke="#333" strokeWidth="1.5"/>
            <line x1={438} y1={baseY+30} x2={438} y2={baseY+55} stroke="#333" strokeWidth="1.5"/>
            <line x1={18} y1={baseY+55} x2={438} y2={baseY+55} stroke="#333" strokeWidth="1.5"/>
            <text x={228} y={baseY+70} textAnchor="middle" fontSize="12" fill="#333" fontWeight="700">V (applied voltage)</text>

            {states.map(({cx,theta,V,color})=>{
              const R=58, tRad=theta*Math.PI/180;
              const hw=R*Math.sin(tRad);
              const largeArc=theta>90?1:0;
              return (
                <g key={cx}>
                  {/* Droplet */}
                  <path d={`M ${cx-hw},${baseY} A ${R},${R} 0 ${largeArc},1 ${cx+hw},${baseY} Z`}
                    fill="#aed6f1" opacity="0.65" stroke="#2980b9" strokeWidth="2.5"/>
                  {/* θ label */}
                  <text x={cx} y={baseY-R*Math.cos(tRad)-12} textAnchor="middle"
                    fontSize="12" fill={color} fontWeight="700">{theta}°</text>
                  {/* V label */}
                  <text x={cx} y={baseY-R*Math.cos(tRad)-26} textAnchor="middle"
                    fontSize="12" fill={color} fontWeight="700">{V}</text>
                </g>
              );
            })}

            {/* Arrow showing voltage increase */}
            <line x1={60} y1={baseY-60} x2={400} y2={baseY-60}
              stroke="#c0392b" strokeWidth="2" markerEnd="url(#ewArr)"/>
            <text x={230} y={baseY-66} textAnchor="middle" fontSize="12" fill="#c0392b" fontWeight="700">Increasing voltage →  contact angle decreases</text>

            {/* Young-Lippmann equation */}
            <rect x={16} y={220} width={PW-26} height={52} rx={3} fill="#fff5f5" stroke="#f0cece"/>
            <text x={22} y={236} fontSize="11" fill="#7B0D0D" fontWeight="700">Young-Lippmann equation:</text>
            <text x={22} y={253} fontSize="12" fill="#333" fontFamily="'Courier New',monospace">cos θ(V) = cos θ₀ + ε₀ε_r V² / (2 σ_LG d)</text>
            <text x={22} y={269} fontSize="10" fill="#555">d = dielectric thickness; ε_r = relative permittivity; θ₀ = zero-voltage angle</text>
          </>;
        })()}

        {/* Right: Digital microfluidics + applications */}
        <rect x={PW+20} y={10} width={PW} height={H-20} rx={5} fill="#fff" stroke="#f0cece"/>
        <text x={PW+20+PW/2} y={28} textAnchor="middle" fontSize="14" fill="#7B0D0D" fontWeight="700">Digital Microfluidics — Droplet Manipulation</text>

        {(()=>{
          const ox=PW+30;
          // Array of electrodes with a droplet being moved
          const nCols=6, nRows=3, cellW=60, cellH=50;
          const startX=ox, startY=44;

          return <>
            {/* Electrode array */}
            {Array.from({length:nRows},(_,ri)=>
              Array.from({length:nCols},(_,ci)=>{
                const x=startX+ci*cellW, y=startY+ri*cellH;
                const isActive=(ri===1 && (ci===2||ci===3));
                const isMoving=(ri===1 && ci===4);
                return (
                  <g key={`${ri}-${ci}`}>
                    <rect x={x} y={y} width={cellW} height={cellH} rx={2}
                      fill={isActive?'#FEF0F0':isMoving?'#e8f4e8':'#f5f5f5'}
                      stroke={isActive?'#c0392b':isMoving?'#27ae60':'#bbb'}
                      strokeWidth={isActive||isMoving?2:1}/>
                    {isActive && <text x={x+cellW/2} y={y+cellH/2+4} textAnchor="middle"
                      fontSize="10" fill="#c0392b">ON</text>}
                    {!isActive && !isMoving && <text x={x+cellW/2} y={y+cellH/2+4} textAnchor="middle"
                      fontSize="9" fill="#aaa">OFF</text>}
                    {isMoving && <text x={x+cellW/2} y={y+cellH/2+4} textAnchor="middle"
                      fontSize="10" fill="#27ae60">NEXT</text>}
                  </g>
                );
              })
            )}

            {/* Droplet spanning two electrodes */}
            <ellipse cx={startX+2.5*cellW+cellW/2} cy={startY+cellH+cellH/2}
              rx={cellW-4} ry={cellH*0.38}
              fill="#aed6f1" opacity="0.75" stroke="#2980b9" strokeWidth="2.5"/>
            <text x={startX+2.5*cellW+cellW/2} y={startY+cellH+cellH/2+4}
              textAnchor="middle" fontSize="11" fill="#2980b9" fontWeight="700">Droplet</text>

            {/* Motion arrow */}
            <line x1={startX+3.5*cellW+cellW/2} y1={startY+cellH+cellH/2}
                  x2={startX+4.5*cellW+cellW/2} y2={startY+cellH+cellH/2}
              stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#ewArr)"/>

            {/* Operations label */}
            <text x={ox+PW/2-10} y={startY+3*cellH+20} textAnchor="middle"
              fontSize="11" fill="#555" fontWeight="700">Operations: move, split, merge, dispense, mix</text>

            {/* Applications */}
            <rect x={ox} y={startY+3*cellH+32} width={PW-20} height={72} rx={3} fill="#FEF0F0" stroke="#c0392b" strokeWidth="1"/>
            <text x={ox+6} y={startY+3*cellH+48} fontSize="11" fill="#7B0D0D" fontWeight="700">EWOD Applications:</text>
            <text x={ox+6} y={startY+3*cellH+63} fontSize="11" fill="#444">• Lab-on-chip: PCR, immunoassays, DNA analysis</text>
            <text x={ox+6} y={startY+3*cellH+78} fontSize="11" fill="#444">• Adaptive lenses: voltage changes droplet curvature</text>
            <text x={ox+6} y={startY+3*cellH+93} fontSize="11" fill="#444">• e-paper displays: pixel switching by droplet position</text>
            <text x={ox+6} y={startY+3*cellH+100} fontSize="11" fill="#444">• Microfluidic cooling of electronics</text>
          </>;
        })()}
      </svg>
      <div style={S.dCaption}>Electrowetting on Dielectric (EWOD) exploits the Young-Lippmann effect: applying a voltage V across a thin insulating layer beneath a conducting droplet reduces the effective solid-liquid interfacial energy and decreases the contact angle: cosθ(V) = cosθ₀ + ε₀ε_rV²/(2σ_LGd). This reversible, repeatable voltage-controlled wettability allows droplets to be moved, split, merged, and dispensed by switching electrodes in a programmable array — the basis of digital microfluidics. EWOD chips require no moving parts and can manipulate nanolitre to microlitre droplets at millisecond timescales, enabling portable diagnostic devices (PCR, immunoassays) that replace large laboratory instruments.</div>
    </div>
  );
}

// ── Diagram 10: Wettability in Biological Systems ────────────────
function DiagramBioWetting() {
  const W = 900, H = 280;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Biological and Bioinspired Wetting — Nature's Solutions</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', display: 'block' }}>

        {/* Five nature examples side by side */}
        {[
          {
            x:10, w:162, title:'Lotus Leaf',
            color:'#27ae60', fill:'#e8f4e8', stroke:'#27ae60',
            lines:['θ ≈ 162°, Δθ ≈ 3°','Micro-papillae +','nano-wax crystals','→ superhydrophobic','Self-cleaning','(rolling droplets)'],
          },
          {
            x:188, w:162, title:'Gecko Foot',
            color:'#e07b00', fill:'#fff5e6', stroke:'#e07b00',
            lines:['θ ≈ 160°','Setae (hair arrays)','+ spatulae tips','→ van der Waals','adhesion + dry','adhesion'],
          },
          {
            x:366, w:162, title:'Namib Beetle',
            color:'#2980b9', fill:'#eaf3ff', stroke:'#2980b9',
            lines:['Alternating bumps','hydrophilic peaks','+ hydrophobic','troughs','→ fog collection','θ_bump ≈ 40°'],
          },
          {
            x:544, w:162, title:'Pitcher Plant',
            color:'#9b59b6', fill:'#f5e6ff', stroke:'#9b59b6',
            lines:['SLIPS: infused','slippery liquid','on porous solid','→ ultra-low CAH','θ < 15°, Δθ < 1°','Prey capture'],
          },
          {
            x:722, w:168, title:'Human Lung',
            color:'#c0392b', fill:'#FEF0F0', stroke:'#c0392b',
            lines:['Surfactant layer','(DPPC) reduces','σ_LG from 72 to','~25 mJ/m²','Prevents alveolar','collapse'],
          },
        ].map(({ x, w, title, color, fill, stroke, lines }) => (
          <g key={title}>
            <rect x={x} y={10} width={w} height={260} rx={5} fill={fill} stroke={stroke} strokeWidth="1.8"/>
            <text x={x+w/2} y={30} textAnchor="middle" fontSize="13" fill={color} fontWeight="700">{title}</text>
            <line x1={x+8} y1={36} x2={x+w-8} y2={36} stroke={stroke} strokeWidth="1"/>
            {/* Simple pictogram */}
            {title==='Lotus Leaf' && <>
              <ellipse cx={x+w/2} cy={110} rx={55} ry={35} fill="#a9dfbf" stroke={color} strokeWidth="1.5"/>
              <circle cx={x+w/2-10} cy={88} r={14} fill="#aed6f1" opacity="0.7" stroke="#2980b9" strokeWidth="1.5"/>
              <circle cx={x+w/2+20} cy={82} r={10} fill="#aed6f1" opacity="0.7" stroke="#2980b9" strokeWidth="1.5"/>
              {[0,1,2,3,4,5,6,7].map(i=><circle key={i} cx={x+12+i*17} cy={144} r={4} fill="#145214" opacity="0.5"/>)}
            </>}
            {title==='Gecko Foot' && <>
              <rect x={x+20} y={55} width={w-40} height={70} rx={5} fill="#fad7a0" stroke={color} strokeWidth="1.5"/>
              {Array.from({length:12},(_,i)=><line key={i} x1={x+22+i*11} y1={125} x2={x+22+i*11} y2={145} stroke={color} strokeWidth="1.5"/>)}
              {Array.from({length:12},(_,i)=><ellipse key={i} cx={x+22+i*11} cy={148} rx={3} ry={2} fill={color} opacity="0.5"/>)}
            </>}
            {title==='Namib Beetle' && <>
              <ellipse cx={x+w/2} cy={100} rx={50} ry={35} fill="#aaa" stroke={color} strokeWidth="1.5"/>
              {[x+w/2-30,x+w/2,x+w/2+30].map((bx,i)=>(
                <g key={i}>
                  <circle cx={bx} cy={88} r={8} fill="#2980b9" opacity="0.6"/>
                </g>
              ))}
              {[x+w/2-20,x+w/2+20].map((bx,i)=>(
                <circle key={i} cx={bx} cy={108} r={10} fill="#c0392b" opacity="0.3"/>
              ))}
            </>}
            {title==='Pitcher Plant' && <>
              <path d={`M ${x+30},${55} C ${x+15},${80} ${x+15},${130} ${x+30},${145} L ${x+w-30},${145} C ${x+w-15},${130} ${x+w-15},${80} ${x+w-30},${55} Z`}
                fill="#d7bde2" stroke={color} strokeWidth="1.5"/>
              <rect x={x+22} y={80} width={w-44} height={20} rx={3} fill="#9b59b6" opacity="0.4"/>
              <text x={x+w/2} y={94} textAnchor="middle" fontSize="9" fill="#5b2c8d">lubricant layer</text>
              <circle cx={x+w/2} cy={68} r={12} fill="#aed6f1" opacity="0.7" stroke="#2980b9" strokeWidth="1.5"/>
              <text x={x+w/2} y={72} textAnchor="middle" fontSize="8" fill="#2980b9">drop</text>
            </>}
            {title==='Human Lung' && <>
              {[0,1,2].map(i=>(
                <g key={i}>
                  <ellipse cx={x+w/2-20+i*20} cy={95+i*8} rx={18} ry={12} fill="#f5b7b1" stroke={color} strokeWidth="1.5"/>
                  <text x={x+w/2-20+i*20} y={99+i*8} textAnchor="middle" fontSize="7" fill="#7B0D0D">alveolus</text>
                </g>
              ))}
              <path d={`M ${x+20},${140} C ${x+w/2},${120} ${x+w/2},${140} ${x+w-20},${130}`}
                fill="none" stroke={color} strokeWidth="1.8" strokeDasharray="4,2"/>
              <text x={x+w/2} y={158} textAnchor="middle" fontSize="9" fill={color}>surfactant film</text>
            </>}

            {lines.map((l, j) => (
              <text key={j} x={x+w/2} y={176+j*16} textAnchor="middle" fontSize="10.5" fill={color}>{l}</text>
            ))}
          </g>
        ))}
      </svg>
      <div style={S.dCaption}>Nature has evolved a remarkable variety of surface wetting strategies, each optimised for a specific function. The lotus leaf achieves self-cleaning via hierarchical hydrophobicity. The Namib Desert beetle collects drinking water from fog by alternating hydrophilic bumps (where droplets nucleate and grow) and hydrophobic troughs (where grown droplets roll toward the mouth). The pitcher plant uses a slippery liquid-infused porous surface (SLIPS), inspired by SLIPS coatings, to create ultra-low adhesion. Human lung surfactant (dominated by DPPC) dynamically reduces alveolar surface tension from ~72 to ~25 mJ/m² during exhalation, preventing alveolar collapse — its absence causes infant respiratory distress syndrome.</div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────
export default function Hydrophilicity() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Hydrophilicity in Fluid Dynamics</h1>
      <p style={S.p}>
            Hydrophilicity is the measurement of the contact angles between
           surfaces of fluids and solids. The specific fields of hydrophilicity and hydrophobicity measure the affinity of solids
            in contact with water. The scientific community distinguishes between these two terms based on the angle formed between a
             drop of water and the horizontal surface of a solid. If the angle is less than 90 degrees, the surface is said to be 
             hydrophilic while an angle greater than 90 degrees is said to be hydrophobic. Materials with an angle greater than 145 
             degrees are considered super-hydrophobic and have almost no affinity with water.  
        Hydrophilicity — the affinity of a surface or molecule for water — is one of the most consequential surface properties in fluid dynamics, chemistry, biology, and engineering. At its heart, wettability determines how water interacts with every solid surface it encounters: whether it spreads into a thin film, beads into a sphere, wicks through a porous medium, climbs up a capillary, or is repelled entirely. These interactions govern phenomena from the self-cleaning of leaves and the water-harvesting strategies of desert beetles to the performance of microfluidic chips, the efficiency of heat exchangers, the durability of anti-icing coatings, and the mechanical function of the human lung. Understanding wettability requires bridging molecular-scale intermolecular forces, nanometre-scale surface chemistry and texture, microscale fluid mechanics, and macroscale capillary phenomena — making it one of the richest topics at the intersection of fluid dynamics and surface science.
      </p>
 <div style={{display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                                    <img src={Hydrophilicity2} alt="linkages" width="75%"/>
                                </div>
      <InfoBox title="Core Ideas at a Glance" items={[
        "Contact angle θ: primary measure of wettability — θ < 90° hydrophilic, θ > 90° hydrophobic, θ > 150° superhydrophobic",
        "Young's equation: cosθ = (σ_SG − σ_SL)/σ_LG — equilibrium contact angle from three interfacial energies",
        "Molecular origin: H-bonds (~20 kJ/mol) between surface functional groups and water drive wettability",
        "Wenzel model: roughness amplifies wettability — r·cosθ_Y (r ≥ 1 = roughness ratio)",
        "Cassie-Baxter model: air pockets raise contact angle — cosθ_CB = f_s cosθ_Y − (1−f_s)",
        "Capillary length λ_c = √(σ/ρg) ≈ 2.73 mm for water — defines scale of capillary vs gravity",
        "Jurin's Law: h = 2σcosθ/(ρgr) — capillary rise inversely proportional to tube radius",
        "Lucas-Washburn: L² = (rσcosθ/2μ)t — wicking front grows as √t",
        "Contact angle hysteresis Δθ = θ_A − θ_R — governs droplet pinning and rolling",
        "Tanner's Law: R(t) ∝ t^(1/10) for viscous-capillary spreading on complete-wetting surfaces",
        "EWOD: cosθ(V) = cosθ₀ + ε₀ε_rV²/(2σ_LG d) — voltage-controlled wettability for microfluidics",
      ]}/>

      <h2 style={S.h2}>The Wettability Spectrum — Contact Angle Classification</h2>
      <p style={S.p}>
        The contact angle θ is the angle formed at the three-phase contact line where solid, liquid, and vapour meet, measured through the liquid. It is the primary and most direct macroscopic measurement of a surface's affinity for water. The contact angle is not an intrinsic material property alone — it depends on both the surface chemistry (which functional groups are present) and the surface texture (roughness, porosity). The full spectrum spans from superhydrophilic surfaces (θ below 10°, where water spreads instantly into a thin film) to superhydrophobic surfaces (θ above 150°, where water forms near-perfect spheres).
      </p>
      <DiagramContactAngleSpectrum/>
      <Table2 rows={[
        ["Contact angle θ","Angle measured through liquid at three-phase contact line\nEquilibrium value on ideal surface: Young's angle θ_Y"],
        ["Superhydrophilic (θ < 10°)","Water spreads immediately to a thin film; θ → 0° after UV/plasma treatment\nExamples: TiO₂ (photoinduced), plasma-treated glass, SiO₂"],
        ["Hydrophilic (10°–90°)","Water preferentially wets the surface; droplets spread significantly\nExamples: glass (θ≈20°), cotton, nylon, most metals and oxides"],
        ["Neutral (θ ≈ 90°)","Equal balance of wetting and non-wetting interactions\nExamples: PMMA (~74°), some coated polymers"],
        ["Hydrophobic (90°–150°)","Water beads up; limited spreading\nExamples: PDMS (~107°), polystyrene (~90°), paraffin wax (~105°)"],
        ["Superhydrophobic (θ > 150°)","Near-spherical droplets; minimal contact with surface\nRequires both low-energy chemistry AND surface roughness (Cassie-Baxter)"],
        ["Omniphobic","Repels both water AND oils (low-surface-tension liquids)\nRequires re-entrant surface geometry to trap air beneath oil droplets"],
        ["Dynamic vs static θ","Static: at rest;  Advancing θ_A: expanding droplet;  Receding θ_R: retreating\nEquilibrium θ_Y lies between θ_R and θ_A"],
      ]}/>

      <h2 style={S.h2}>Molecular Origin — Hydrogen Bonding and Surface Energy</h2>
      <p style={S.p}>
        Hydrophilicity is fundamentally a molecular phenomenon. Water is a strongly polar molecule (dipole moment 1.85 D) with an oxygen atom capable of accepting two hydrogen bonds and two hydrogen atoms capable of donating two more — making up to four simultaneous hydrogen bonds per molecule possible. A surface is hydrophilic when its exposed groups can participate in these H-bonding or strong polar interactions with water molecules, releasing significant energy upon wetting. Surfaces with high surface energy (metals, metal oxides, glass, hydroxylated polymers) achieve this readily; low-surface-energy materials (fluorocarbons, hydrocarbons, silicones) cannot and are hydrophobic.
      </p>
      <DiagramMolecularOrigin/>
      <Table2 rows={[
        ["Hydrogen bond","O–H···O interaction;  energy ~10–40 kJ/mol\nArises from partial charges: O (δ−) attracts H (δ+) of adjacent molecule"],
        ["Surface energy γ","Energy per unit area required to create a new surface (mJ/m²)\nHigh γ (metals, oxides: 500–5000 mJ/m²) → strong wetting\nLow γ (PTFE: 18, PDMS: 22 mJ/m²) → hydrophobic"],
        ["Polar component γ^P","Contribution from dipole-dipole, H-bond interactions\nHydrophilic surfaces: large γ^P (glass: γ^P ≈ 30 mJ/m²)"],
        ["Dispersive component γ^D","London (van der Waals) dispersion — present in ALL materials\nHydrophobic surfaces: dominated by γ^D, small γ^P"],
        ["Owens-Wendt theory","γ_SL = γ_S + γ_L − 2(√(γ_S^D γ_L^D) + √(γ_S^P γ_L^P))\nAllows calculation of θ from surface energy components"],
        ["Hydrophilic functional groups","–OH (hydroxyl): strongest wetter;  –COOH, –NH₂, –SO₃H also hydrophilic\nPresence on polymer surface dramatically lowers θ"],
        ["Hydrophobic groups","–CH₃, –CF₃, –CH₂– chains shield polar backbone from water\nPEG (polyethylene glycol) unusual: hydrophilic despite CH₂ backbone due to ether O"],
        ["Surface contamination","Any carbonaceous contamination on glass/metal raises θ from near-zero to 30–60°\nOzone or UV/ozone cleaning removes organics → superhydrophilic TiO₂"],
      ]}/>

      <h2 style={S.h2}>Wetting Models — Young, Wenzel, and Cassie-Baxter</h2>
      <p style={S.p}>
        Real surfaces are neither perfectly flat nor chemically homogeneous. Two classical models extend Young's equation to account for roughness. Wenzel (1936) treated a drop that fully wets the rough surface — liquid penetrates all valleys. This amplifies the intrinsic wettability: a hydrophilic surface becomes more hydrophilic, a hydrophobic one more hydrophobic. Cassie and Baxter (1944) treated a drop that sits on top of the roughness with air trapped beneath — a composite solid-gas interface. This always increases the apparent contact angle regardless of intrinsic chemistry, and can produce superhydrophobicity even from a moderately hydrophobic material.
      </p>
      <DiagramWettingModels/>
      <Table2 rows={[
        ["Young's equation","cosθ_Y = (σ_SG − σ_SL) / σ_LG\nThermodynamic equilibrium for ideal flat, rigid, homogeneous surface\nDerived by minimising free energy of three-phase system"],
        ["Roughness factor r","r = actual surface area / projected (flat) area;  r ≥ 1 always\nFor sinusoidal surface: r = E(−e²)/(period);  pillars: r = 1 + πd h/(pitch²)"],
        ["Wenzel equation","cosθ_W = r cosθ_Y\nθ_W < θ_Y when θ_Y < 90° (more hydrophilic)\nθ_W > θ_Y when θ_Y > 90° (more hydrophobic)"],
        ["Solid fraction f_s","Fraction of droplet base in contact with solid (not air)\nFor cylindrical pillars: f_s = π d²/(4 × pitch²)"],
        ["Cassie-Baxter equation","cosθ_CB = f_s cosθ_Y − (1 − f_s)\nAs f_s → 0: cosθ_CB → −1, θ_CB → 180°\nLow f_s → high apparent θ AND low CAH (easier rolling)"],
        ["Wenzel-Cassie transition","High pressure (Impact, evaporation) can force liquid into pores → Wenzel state\nCassie → Wenzel irreversible for many designs (petal effect)\nDynamic pressure criterion: ΔP > 2σ_LG cosθ_Y / r_pore"],
        ["Petal effect","High θ (Cassie) BUT high hysteresis — droplets sit on rose petals without rolling\nRe-entrant geometry with microstructure penetrated by liquid but nanoscale still air-filled"],
        ["Impregnating regime","Some liquids spontaneously wet pore tops → partial wetting (Wenzel) + air below\nImportant in SLIPS (Slippery Liquid Infused Porous Surfaces)"],
      ]}/>

      <h2 style={S.h2}>Capillary Rise and the Capillary Length</h2>
      <DiagramCapillaryRise/>
      <Table2 rows={[
        ["Young-Laplace equation","Δp = σ(1/R₁ + 1/R₂) — pressure jump across curved interface\nFor meniscus in tube radius r: Δp = 2σcosθ/r (both radii = r/cosθ → R = r/cosθ)"],
        ["Jurin's Law","h = 2σcosθ/(ρgr)\nWater in glass (σ=72.8 mN/m, θ=20°, r=0.1mm): h ≈ 138 mm\nMercury in glass (θ=140°): depressed, h < 0 (capillary depression)"],
        ["Capillary length λ_c","λ_c = √(σ/ρg);  water: λ_c ≈ 2.73 mm at 20°C\nBelow λ_c: surface tension important;  above: gravity important"],
        ["Bond number Bo","Bo = L²/λ_c² = ρgL²/σ\nBo ≪ 1: capillary-dominated (small drops, microfluidics, insects)\nBo ≫ 1: gravity-dominated (large pools, rain drops)"],
        ["Meniscus shape","Profile follows modified Laplace equation with gravity term\ny(x) ∝ exp(−x/λ_c) for flat wall;  exact: y = λ_c arccosh(λ_c/x)"],
        ["Tree sap ascent","Cohesion-tension theory: transpiration creates tension (negative pressure) in xylem\nCapillary vessels r ≈ 10–20 μm: theoretical h up to 150 m (matches tallest trees)"],
        ["Capillary pressure","p_c = 2σcosθ/r (capillary tubes);  critical for oil recovery, soil water retention\nBrooks-Corey model: p_c = p_e (S_e)^(−1/λ) for porous media"],
        ["Electrocapillarity","Electric field modifies σ_SL and hence θ (Lippmann equation)\nBasis of EWOD;  max capillary force at contact angle saturation"],
      ]}/>

      <h2 style={S.h2}>Contact Angle Hysteresis and Droplet Pinning</h2>
      <DiagramHysteresis/>
      <Table2 rows={[
        ["Advancing angle θ_A","Maximum contact angle before contact line moves forward\nMeasured by slowly adding liquid to a droplet (inflation method)"],
        ["Receding angle θ_R","Minimum contact angle before contact line retreats\nMeasured by slowly removing liquid (deflation method) or tilting"],
        ["Hysteresis Δθ","Δθ = θ_A − θ_R;  ideal surface: Δθ = 0\nClean glass: Δθ ≈ 10–20°;  lotus: Δθ ≈ 3°;  rough polymer: Δθ ≈ 30–50°"],
        ["Retention force","F_ret = σ_LG w (cosθ_R − cosθ_A)  (w = droplet width)\nForce needed to set droplet in motion on tilted surface"],
        ["Sliding angle α*","mg sinα* = F_ret  →  α* depends on both θ and Δθ\nLotus leaf: α* ≈ 3°;  standard hydrophobic: α* ≈ 30°"],
        ["Pinning mechanisms","Chemical: defect patches with different θ_Y trap contact line\nGeometric: sharp edges (Gibbs criterion) pin the contact line\nElastic: deformation of soft substrate (wetting ridge)"],
        ["Stick-slip motion","Contact line is stationary until local energy barrier overcome\nResults in jerky droplet motion, visible in time-lapse imaging"],
        ["Measurement methods","Sessile drop (goniometry);  Wilhelmy plate;  tilting plate;  ADSA (axisymmetric drop shape analysis)"],
      ]}/>

      <h2 style={S.h2}>Spreading Dynamics</h2>
      <DiagramSpreadingDynamics/>
      <Table2 rows={[
        ["Spreading coefficient S","S = σ_SG − σ_SL − σ_LG\nS > 0: complete wetting, θ_eq → 0°;  S < 0: partial wetting, finite θ_eq"],
        ["Inertial spreading regime","Very early time: R ~ t^(1/2); inertia balances surface tension\nCapillary-inertia velocity: u ~ √(σ/ρR)"],
        ["Tanner's Law","Viscous-capillary regime: R(t) = C(σ/μ)^(1/10) · V^(3/10) · t^(1/10)\nV = droplet volume;  C ≈ 0.7 numerical constant"],
        ["Viscous dissipation at contact line","Most dissipation occurs within ~nm of moving contact line\nExplains slow spreading despite large driving force"],
        ["Molecular kinetic theory","Contact line motion controlled by thermally activated molecular jumps\nθ ≠ 0 even during spreading;  friction from surface-molecule interactions"],
        ["Precursor film","On complete-wetting surfaces, a thin film (~nm) runs ahead of visible contact line\nDriven by van der Waals disjoining pressure;  discovered by Hardy (1919)"],
        ["Disjoining pressure","Π(h) = A/(6πh³) + electrostatic + structural terms\nStabilises thin films;  A = Hamaker constant;  repulsive for wetting films"],
        ["Spontaneous imbibition","In porous media: gravity-free Lucas-Washburn L = √(rσcosθ t / 2μ)\nUsed in paper microfluidics (lateral flow assays)"],
      ]}/>

      <h2 style={S.h2}>Wicking and Imbibition in Porous Media</h2>
      <DiagramWicking/>
      <Table2 rows={[
        ["Capillary pressure in pores","p_c = 2σcosθ/r_eff  (effective pore radius)\nDriving force for imbibition;  counteracted by gravity and viscous pressure drop"],
        ["Lucas-Washburn equation","L(t) = √(r σ cosθ t / 2μ)\nAssumes: cylindrical pores, quasi-steady meniscus, no gravity\nValid when Bo = ρgL²/σ ≪ 1 (early time or horizontal)"],
        ["Inertial correction","Early times: meniscus acceleration important\nWashburn-Rideal: ρr/8 · d²L/dt² + (8μ/r²) L dL/dt = 2σcosθ/r"],
        ["Darcy's law","u = −(K/μ)∇p;  K = permeability (m²)\nFor packed spheres (Kozeny-Carman): K = d_p²ε³/[180(1−ε)²]\nε = porosity, d_p = particle diameter"],
        ["Wicking rate","dL/dt = (r σ cosθ)/(4μL) — slows down as L grows\nExperiment: plot L² vs t → slope gives r σ cosθ/2μ"],
        ["Textile wicking","Fabrics: both inter-yarn capillaries (large, fast fill) and intra-yarn (slow fill)\nKes model accounts for contact angle on fibre surface and yarn geometry"],
        ["Paper chromatography","Capillary-driven lateral flow in cellulose fibre network\nLateral flow assays (pregnancy, COVID tests): ~15 min wicking time over ~4 cm"],
        ["Soil water retention","Water held in soil by capillarity: suction ψ = −2σcosθ/r\nField capacity (θ ≈ 33 kPa), permanent wilting point (θ ≈ 1500 kPa)"],
      ]}/>

      <h2 style={S.h2}>Superhydrophobic Surfaces and the Lotus Effect</h2>
      <DiagramSuperhydrophobic/>
      <Table2 rows={[
        ["Lotus effect requirements","θ > 150° AND Δθ < 5°  (both high θ AND low hysteresis)\nRequires: low-energy chemistry + hierarchical roughness (micro + nano)"],
        ["Cassie parameter f_s","Typical lotus: f_s ≈ 0.02–0.05 (2–5% solid contact)\nCalculated: cosθ_CB = f_s cosθ_Y + f_s − 1 → very high θ_CB"],
        ["Air plastron","Thin continuous air layer trapped beneath submerged superhydrophobic surface\nReduces skin friction by >50% in some configurations\nDragonfly wing surfaces retain air when submerged"],
        ["Manufacturing methods","Electrospinning, etching (acid/laser), templating, sol-gel, nanoparticle coatings\nHierarchical: first create micro-texture then nano-texture on top"],
        ["Durability issues","Cassie-Wenzel transition under mechanical abrasion or high impact\nOzone/UV degrades low-energy organic coatings\nActive research: self-healing coatings that restore nano-roughness"],
        ["Rose petal effect","High θ (>150°) BUT high CAH;  droplets adhere rather than roll\nWenzel at micro-scale, Cassie at nano-scale: pinning + high angle"],
        ["Anti-icing","Superhydrophobic surfaces delay ice nucleation (fewer nucleation sites)\nBut once ice forms, adhesion may be higher than smooth surface\nDynamic anti-icing requires both low-θ (ice-phobic) AND mechanical properties"],
        ["Drag reduction","Air plastron on superhydrophobic underwater surfaces: effective slip length b = (1−f_s)λ/f_s\nb ≈ 20–200 μm → drag reduction 10–30% in laminar flow"],
      ]}/>

      <h2 style={S.h2}>Electrowetting and Active Wettability Control</h2>
      <DiagramElectrowetting/>
      <Table2 rows={[
        ["Young-Lippmann equation","cosθ(V) = cosθ₀ + ε₀ε_r V² / (2σ_LG d)\nLinear in V²;  θ decreases with |V|;  reversible (AC or DC)\nd = dielectric thickness;  ε_r = dielectric constant"],
        ["Contact angle saturation","At high V, θ stops decreasing: θ_sat ≈ 30–40°\nMechanisms: charge trapping in dielectric, contact line instability\nPrevents reaching θ = 0° even at large voltages"],
        ["EWOD materials","Dielectric: Parylene C (ε_r=3.2), SU-8 (ε_r=4), Cytop (ε_r=2.1)\nHydrophobic coating on top: PTFE, Cytop (θ₀ ≈ 110–120°)\nElectrode: ITO (transparent), gold, aluminium"],
        ["Digital microfluidics","Droplet velocities: 10–100 mm/s;  volumes: 1–1000 nL\nOperations: transport, split, merge, mix, dispense\nNo pumps or valves — entire lab function on mm-scale chip"],
        ["Thermocapillary flow (Marangoni)","σ decreases with T;  thermal gradient → surface tension gradient → flow\nσ_water: dσ/dT ≈ −0.18 mN/m·K\nDroplet moves from cold (high σ) to hot (low σ) region"],
        ["Photo-wetting (TiO₂)","UV irradiation of TiO₂: photocatalytically destroys organics, hydroxylates surface\nθ drops from ~70° to near 0° under UV;  recovers in dark (~24 h)\nUsed in self-cleaning glass (Pilkington Activ™)"],
        ["Magnetic ferrofluid wetting","Ferrofluid contact angle modulated by external magnetic field\nH-field deforms droplet shape, modifying spreading\nApplications: microfluidic valves, adaptive optics"],
        ["Acoustic (SAW) wetting","Surface acoustic waves actuate droplets by radiation pressure\nNo electrode patterning needed;  GHz frequency;  mixing in milliseconds"],
      ]}/>

      <h2 style={S.h2}>Biological and Bioinspired Wetting</h2>
      <DiagramBioWetting/>
      <Table2 rows={[
        ["Lotus leaf (Nelumbo nucifera)","θ ≈ 162°, Δθ ≈ 3°, sliding angle ≈ 3°\nMicropapillae 10–15 μm + epicuticular wax nano-crystals 100–300 nm\nBioinspired: self-cleaning paints (Sto Lotusan), textiles, photovoltaics"],
        ["Gecko adhesion","Setae (100 μm) + spatulae (200 nm) → ~10⁸ spatulae per foot\nVan der Waals adhesion: 10 atm normal stress\nSuperhydrophobic but strong dry adhesion: not a wetting effect"],
        ["Namib Desert beetle","Alternating hydrophilic bumps (θ≈40°) and hydrophobic wax troughs (θ≈115°)\nFog droplets nucleate on bumps, grow, slide to mouth via hydrophobic troughs\nInspired fog collectors: Shreerang Chhatre's bio-inspired nets"],
        ["SLIPS (pitcher plant)","Slippery Liquid-Infused Porous Surface (Aizenberg, Harvard)\nLubricant (silicone oil) infuses porous PTFE → θ_water < 5°, Δθ < 1°\nRepels: water, oil, ice, bacteria, blood;  transparent and self-healing"],
        ["Lung surfactant","DPPC (dipalmitoyl phosphatidylcholine) monolayer at alveolar air-water interface\nReduces σ from 72 mJ/m² at inspiration to ~25 mJ/m² at expiration\nCritical: without it, alveoli collapse (IRDS in premature infants)"],
        ["Water strider legs","Hierarchical hydrophobic microsetae on legs: θ ≈ 168°\nSupports insect weight by surface tension (F = σ × 2L_leg × sinθ)\nMaximum depth of deformation ≈ 4 mm before sinking"],
        ["Mussel adhesion proteins","DOPA (3,4-dihydroxy-L-phenylalanine): forms H-bonds and coordinate bonds\nAdheres strongly to wet surfaces: shear strength ~1 MPa underwater\nInspired bio-adhesives, wet surgical glues"],
        ["Plant stomata","Guard cells regulate water loss by opening/closing 1–10 μm pore\nHydrophilic cell walls — capillary forces contribute to turgor pressure\nGlobal transpiration: ~500,000 km³ water/year moves through stomatal pores"],
      ]}/>

      <h2 style={S.h2}>Wetting in Confined Geometry — Microfluidics and Nanofluidics</h2>
      <Table2 rows={[
        ["Microfluidic regimes","Re ≪ 1: Stokes flow;  Ca = μU/σ governs interface deformation\nCa ≪ 1: surface tension dominant;  Ca ≈ 1: viscous and capillary balance"],
        ["T-junction droplet generation","Dispersed phase jet enters cross-flow of continuous phase\nDroplet size: d ~ (σ/μ_c) × (Q_d/Q_c)^(n);  n ≈ 0.4–0.7\nMonodisperse emulsions with CV < 2%"],
        ["Capillary electrophoresis","Charged molecules migrate under E-field through capillary\nElectroosmotic flow (EOF): double-layer drag moves bulk fluid\nHydrophilic silica capillary: strongly cathodic EOF"],
        ["Electro-osmotic flow","u_EOF = −(ε_0 ε_r ζ / μ) E  (Helmholtz-Smoluchowski)\nζ = zeta potential (surface charge × double-layer);  hydrophilic → high |ζ|"],
        ["Contact line in nanochannels","Wall effects dominate: viscosity near wall different from bulk\nMolecular dynamics: slip length on hydrophobic surfaces b ~ 20–100 nm\nHydrophilic: b ≈ 0 (no-slip);  Hydrophobic: b > 0 (partial slip)"],
        ["Capillary filling nanochannels","At r < 100 nm: viscosity increase and structure in thin water films\nFilling dynamics deviate from Lucas-Washburn;  polarisation effects\nMD simulations required for quantitative accuracy"],
        ["Paper microfluidics (μPAD)","Lateral flow driven by capillary action through nitrocellulose or cellulose\nNo pumps, no power;  wax printing defines hydrophobic barriers\nCOVID-19, malaria, pregnancy tests — billions produced annually"],
        ["Droplet microfluidics (DMF)","Oil-water-oil droplets as discrete reaction vessels in microchannels\nEncapsulate cells, perform PCR, screen compounds\nCapillary number Ca controls droplet formation and break-up"],
      ]}/>

      <h2 style={S.h2}>Industrial and Engineering Applications</h2>
      <Table2 rows={[
        ["Heat transfer enhancement","Hydrophilic surfaces: dropwise condensation → drops shed fast → high h\nPool boiling: nucleate boiling onset at lower superheat on hydrophilic\nSuperhydrophobic: delayed film boiling transition (Leidenfrost point raised)"],
        ["Anti-fouling coatings","PEG (polyethylene glycol) brush: steric repulsion prevents protein adsorption\nZwitterionic polymers: ultra-low fouling on medical implants, membranes\nSLIPS coatings: resist bacterial biofilm, ice, oil, barnacles"],
        ["Membrane separation","Hydrophilic membranes: high water permeability, low fouling in water treatment\nHydrophobic membranes: membrane distillation (only vapour passes)\nJanus membranes: directional water transport (one side hydrophilic, other hydrophobic)"],
        ["Oil-water separation","Hydrophilic-oleophobic or superhydrophobic-superoleophilic meshes\nGravity-driven separation;  used in oil spill cleanup, produced water treatment"],
        ["Inkjet printing","Ink surface tension balanced with substrate wettability for controlled spreading\nContact angle θ determines droplet spread diameter and print resolution\nInk-paper pair engineered for θ ≈ 15–30° for sharp dots"],
        ["Agricultural spraying","Pesticide droplets must wet the waxy leaf surface (θ > 90° without adjuvant)\nSurfactants (adjuvants) reduce σ and θ; increase coverage\nOver-treatment: runoff to soil; under-treatment: pest control failure"],
        ["Concrete and masonry","Silane/siloxane impregnation makes pores superhydrophobic\nPrevents water ingress → reduces freeze-thaw damage, corrosion\nDiffusion-resistant but allows moisture vapour to escape (breathable)"],
        ["Fuel cells (PEM)","Water management critical: membranes must be hydrated (ionic conductivity)\nGas diffusion layer (GDL) must be hydrophobic to prevent water flooding\nHydrophilic/hydrophobic patterning in flow field plates optimises water removal"],
      ]}/>

      <InfoBox title="Key Formulae Summary" items={[
        "Young's equation:  cosθ = (σ_SG − σ_SL) / σ_LG",
        "Wenzel model:  cosθ_W = r cosθ_Y   (r = roughness ratio ≥ 1)",
        "Cassie-Baxter:  cosθ_CB = f_s cosθ_Y − (1 − f_s)",
        "Jurin's Law (capillary rise):  h = 2σ cosθ / (ρgr)",
        "Capillary length:  λ_c = √(σ / ρg)  ≈ 2.73 mm for water",
        "Lucas-Washburn (wicking):  L(t) = √(rσ cosθ · t / 2μ)",
        "Tanner's Law (spreading):  R(t) ∝ (σ/μ)^(1/10) t^(1/10)",
        "Young-Laplace (pressure jump):  Δp = σ(1/R₁ + 1/R₂)",
        "Droplet retention force:  F = σ_LG w (cosθ_R − cosθ_A)",
        "Young-Lippmann (EWOD):  cosθ(V) = cosθ₀ + ε₀ε_r V² / (2σ_LG d)",
        "Spreading coefficient:  S = σ_SG − σ_SL − σ_LG;  S > 0 → complete wetting",
        "Bond number:  Bo = ρgL²/σ;  Capillary number:  Ca = μU/σ",
      ]}/>
    </div>
  );
}